/**
 * MODULAR AI SERVICE - MEDICAL ARCHITECTURE
 * STRATEGY: OPTION A (SYNCHRONIZATION)
 * This file contains the core AI logic for MODULAR. 
 * The models and prompts here are designed to be scanned and whitelisted by the platform's PROXY_CONFIG.
 */
import { GoogleGenAI, HarmCategory, HarmBlockThreshold, Type, ThinkingLevel } from "@google/genai";
import { ReportTemplate, GeminiContent, SystemPricing, AppSuggestion, LatencyModule, UserProfile } from "../types";
import { recordTokenUsage, saveLatencyEvent, acquireGlobalLock } from "./db";
import { auth } from "../firebaseConfig";
import { getAudioDuration, blobToBase64 } from "./audioUtils";
import { logAppError } from "./errorLogger";
import { normalizeMedicalText } from "../utils/medicalNormalizer";

export const GEMINI_MODELS = {
  FAST: "gemini-3.1-flash-lite", // Ecosistema rápido validado
  NORMAL: "gemini-3.5-flash"     // Modelo completo de alta capacidad (Resuelve el 404 y 429)
};

const SAFETY_SETTINGS = [
  { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
  { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
];

/**
 * Extracción robusta de JSON para evitar errores "Unexpected character after JSON"
 * cuando el modelo añade texto fuera de las llaves por error.
 */
const robustJSONParse = (text: string): any => {
  if (!text) return {};
  try {
    // Intentamos el parse directo por si viene limpio
    return JSON.parse(text);
  } catch (e) {
    // Si falla, buscamos el primer '{' o '[' y el último '}' o ']' para extraer solo la estructura JSON
    // Buscamos tanto objetos {} como arrays []
    const jsonMatch = text.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]);
      } catch (innerError) {
        console.error("Critical JSON Extraction Failure", innerError);
        throw innerError;
      }
    }
    throw e;
  }
};

/**
 * Logs token usage to the database.
 */
export const logUsage = (response: any, model: string, hasSearch: boolean = false) => {
  const userId = auth.currentUser?.uid;
  if (!userId || !response.usageMetadata) return;
  
  const { promptTokenCount, candidatesTokenCount } = response.usageMetadata;
  recordTokenUsage(userId, model, promptTokenCount, candidatesTokenCount, hasSearch);
};

const logLatency = async (module: LatencyModule, startTime: number, status: 'SUCCESS' | 'ERROR' | 'TIMEOUT', error?: any, payloadSize?: number, metadata?: any) => {
  const durationMs = Date.now() - startTime;
  try {
    await saveLatencyEvent({
      module,
      durationMs,
      status,
      errorCode: error?.name || error?.status?.toString() || (error ? "UNKNOWN_ERROR" : undefined),
      errorMessage: error?.message,
      payloadSize,
      metadata
    });
  } catch (e) {
    console.error("Critical Telemetry Failure", e);
  }
};

/**
 * Limpia la respuesta de la IA de caracteres de escape literales como \n
 * que a veces el modelo devuelve por error en lugar de saltos de línea reales.
 */
export function cleanAIResponse(aiString: string): string {
  if (!aiString) return "";
  
  let cleaned = aiString
    .replace(/\\n\\n/g, "\n\n") // Convierte dobles escapes de la IA en saltos dobles reales
    .replace(/\\n/g, "\n")     // Convierte escapes simples
    .replace(/\r/g, "");        // Limpia retornos de carro residuales de Windows

  // 🛡️ FILTRO DEFENSIVO CONTRA LISTAS (RadIA Style)
  // Elimina números o letras al inicio de línea (ej: "1. ", "2) ", "a. ")
  cleaned = cleaned.replace(/^\s*\d+[.)]\s*/gm, "");
  cleaned = cleaned.replace(/^\s*[a-zA-Z][.)]\s*/gm, "");
  
  // Elimina guiones, asteriscos o viñetas al inicio de línea (ej: "- ", "* ", "• ")
  cleaned = cleaned.replace(/^\s*[-*•]\s*/gm, "");

  return cleaned;
}

const getApiKey = () => {
  // Con el Proxy en el Servidor, el cliente ya no necesita la clave real.
  // Sin embargo, el SDK exige un string no vacío para pasar la validación de inicialización.
  return "SERVER_PROXY_KEY";
};

/**
 * Interceptor de fetch para inyectar token de autenticación fresco de Firebase
 * EXCLUSIVAMENTE en peticiones dirigidas a /api/gemini o /gemini-api-proxy.
 */
if (typeof window !== "undefined" && !(window as any).__RADIA_GEMINI_FETCH_INTERCEPTOR_INSTALLED__) {
  try {
    (window as any).__RADIA_GEMINI_FETCH_INTERCEPTOR_INSTALLED__ = true;
    const nativeFetch = window.fetch.bind(window);
    const customFetch = async (input: any, init?: any) => {
      let urlString = "";
      if (typeof input === "string") {
        urlString = input;
      } else if (input && typeof input.href === "string") {
        urlString = input.href;
      } else if (input && typeof input.url === "string") {
        urlString = input.url;
      } else if (input && typeof input.toString === "function") {
        urlString = input.toString();
      }

      if (urlString.includes("/api/gemini") || urlString.includes("/gemini-api-proxy")) {
        const user = auth.currentUser;
        if (user) {
          try {
            // Token fresco (getIdToken() refresca si está vencido o próximo a expirar)
            const token = await user.getIdToken();
            const headers = new Headers(init?.headers || (input && input.headers ? input.headers : {}));
            headers.set("Authorization", `Bearer ${token}`);
            
            if (typeof Request !== "undefined" && input instanceof Request) {
              const modifiedRequest = new Request(input, { ...init, headers });
              return nativeFetch(modifiedRequest);
            } else {
              return nativeFetch(input, { ...init, headers });
            }
          } catch (tokenErr) {
            console.warn("[Gemini Fetch Interceptor] No se pudo obtener token de Auth:", tokenErr);
          }
        }
      }
      return nativeFetch(input, init);
    };

    try {
      Object.defineProperty(window, "fetch", {
        value: customFetch,
        writable: true,
        configurable: true,
      });
    } catch {
      try {
        (window as any).fetch = customFetch;
      } catch (assignErr) {
        console.warn("[Gemini Fetch Interceptor] Could not override window.fetch directly:", assignErr);
      }
    }
  } catch (err) {
    console.warn("[Gemini Fetch Interceptor] Failed to install interceptor:", err);
  }
}

const getAIClient = () => {
  const isBrowser = typeof window !== "undefined";
  const config: any = {
    apiKey: getApiKey(),
  };

  if (isBrowser) {
    // CORRECTO: httpOptions.baseUrl (u minúscula). Antes usábamos config.baseURL
    // que NO existe en el SDK @google/genai y se ignoraba silenciosamente.
    config.httpOptions = {
      baseUrl: `${window.location.origin}/api/gemini`,
    };
  }

  return new GoogleGenAI(config);
};

/**
 * Formatea errores de la API de Gemini para que sean legibles y útiles para el usuario.
 */
const formatGeminiError = (e: any, context: string): string => {
  // Mantenemos el log detallado para diagnóstico en consola
  console.error(`[System Diagnostic - ${context}]:`, e);
  
  if (e.name === 'AbortError' || e.message?.includes('timeout') || e.message?.includes('deadline')) {
    return "Tiempo de espera agotado (15s): El procesamiento está tomando más de lo habitual. Por favor, reintente con un audio más corto o verifique su conexión.";
  }

  // Mensaje base amigable
  let message = "El sistema de soporte no ha podido procesar la solicitud en este momento. ";
  
  if (e.message) {
    if (e.message.includes("API_KEY_INVALID") || e.message.includes("API key not valid")) {
      return "Error de acceso: Por favor, verifique la configuración de su licencia en Ajustes.";
    } else if (e.message.includes("Quota exceeded") || e.message.includes("429")) {
      return "Alta demanda detectada: El motor de procesamiento necesita unos segundos. Por favor, reintente en breve.";
    } else if (e.message.includes("User location is not supported")) {
      return "Servicio no disponible en su región actual.";
    } else if (e.message.includes("Safety") || e.message.includes("blocked")) {
      return "La revisión de seguridad ha pausado la respuesta. Por favor, reformule su petición.";
    }
  }

  // Errores de red o latencia
  if (e.status === 502 || e.status === 503 || e.status === 504) {
    return "Fallo de conexión temporal con el servicio de procesamiento médico. Por favor, intente de nuevo.";
  }
  
  return message + "No se preocupe, puede intentar de nuevo en unos instantes.";
};

/**
 * BUSCADOR DE PRECIOS PhD: Utiliza Google Search Grounding para encontrar las tarifas oficiales.
 */
export interface ProfessionalVerificationResult {
  isValid: boolean;          // true si confidenceScore >= 90
  confidenceScore: number;   // 0 a 100
  matchLevel: 'ALTA' | 'MEDIA' | 'BAJA' | 'NULA';
  isRadiologistOrDoctor: boolean;
  reason: string;            // Informe explicativo estructurado para validationReport
  foundAffiliations?: string[];
  summaryConclusion?: string;
}

/**
 * AUDITORÍA DE IDENTIDAD PROFESIONAL:
 * Utiliza gemini-3.5-flash y Google Search Grounding para contrastar si el usuario
 * es un médico radiólogo real o profesional acreditado.
 * Regla de pre-aprobación estricta: confidenceScore >= 90.
 */
export const verifyProfessionalIdentity = async (
  profileOrLinkedin: Partial<UserProfile> | string,
  legacyName?: string,
  legacyCollege?: string
): Promise<ProfessionalVerificationResult> => {
  const ai = getAIClient();
  const modelId = GEMINI_MODELS.NORMAL; // 'gemini-3.5-flash'
  
  // Normalización polimórfica (acepta tanto objeto UserProfile como parámetros string heredados)
  let profile: Partial<UserProfile> = {};
  if (typeof profileOrLinkedin === 'string') {
    profile = {
      linkedin: profileOrLinkedin,
      displayName: legacyName || '',
      college: legacyCollege || ''
    };
  } else if (profileOrLinkedin && typeof profileOrLinkedin === 'object') {
    profile = profileOrLinkedin;
  }

  const name = profile.displayName || '';
  const linkedin = profile.linkedin || '';
  const college = profile.college || '';
  const collegeNumber = profile.collegeNumber || '';
  const dni = profile.dni || '';
  const workplace = profile.workplace || '';
  const mainSpecialty = profile.mainSpecialty || '';
  const specialties = profile.specialties?.join(', ') || '';
  const locationStr = [profile.workCity, profile.workCountry || profile.country].filter(Boolean).join(', ');
  const role = profile.role || 'RADIOLOGIST';
  const residentYear = profile.residentYear ? `R${profile.residentYear}` : '';
  const otherSpecialty = profile.otherMedicalSpecialty || '';

  const prompt = `ERES UN AUDITOR MÉDICO FORENSE Y ANALISTA DE IDENTIDAD CLÍNICA EN RADIOLOGY AI ASSISTANT.
Tu tarea es contrastar exhaustivamente mediante búsqueda web (Google Search Grounding) si la siguiente persona es un MÉDICO RADIÓLOGO REAL (o profesional médico colegiado / residente de radiología).

DATOS DECLARADOS DEL PROFESIONAL:
- Nombre y Apellidos: ${name || 'No especificado'}
- Especialidad Principal: ${mainSpecialty || 'No especificada'}
- Especialidades Adicionales / Áreas: ${specialties || otherSpecialty || 'Ninguna'}
- Rol / Nivel: ${role} ${residentYear}
- Centro de Trabajo / Hospital: ${workplace || 'No especificado'}
- Colegio Médico / Institución: ${college || 'No especificado'}
- Número de Colegiado: ${collegeNumber || 'No especificado'}
- DNI / Identificación: ${dni || 'No especificado'}
- Ubicación de Ejercicio: ${locationStr || 'No especificada'}
- Enlace / Perfil LinkedIn: ${linkedin || 'No especificado'}

PAUTAS DE BÚSQUEDA Y EVALUACIÓN:
1. Realiza búsquedas cruzadas del nombre del profesional combinando con su especialidad médica, hospital/centro de trabajo, ciudad, colegio de médicos y enlaces de LinkedIn en fuentes públicas, cuadros médicos, sociedades científicas (SERAM, ESR, RSNA, etc.) o directorios profesionales.
2. Determina un PORCENTAJE DE CERTEZA DE CORRESPONDENCIA (confidenceScore entre 0 y 100):
   - 90% a 100% (Coincidencia ALTA): Evidencias inequívocas o contrastables de que es un radiólogo o médico real colegiado/en ejercicio.
   - 70% a 89% (Coincidencia MEDIA): Indicios verosímiles pero con información pública fragmentaria o sin confirmación institucional directa.
   - 40% a 69% (Coincidencia BAJA): Datos genéricos, presencia de homónimos no diferenciables o discordancias leves.
   - 0% a 39% (Coincidencia NULA): Sin rastro en registros médicos, inconsistencias graves o indicios de perfil no médico.
3. UMBRAL DE PRE-APROBACIÓN: "isValid" DEBE ser true ÚNICAMENTE SI confidenceScore >= 90. Si confidenceScore < 90, "isValid" DEBE ser false.
4. Genera un INFORME ESTRUCTURADO Y EXPLICATIVO para el recuadro de auditoría del usuario Master, con el siguiente desglose:
   - 🎯 Índice de Certeza: [X]% (Coincidencia [ALTA/MEDIA/BAJA/NULA])
   - 🩺 Condición Radiológica / Médica: [Detalle de si ejerce como radiólogo o médico real y concordancia de especialidad]
   - 🏛️ Fuentes y Afiliaciones Contrastadas: [Hospitales, instituciones, perfiles o directorios localizados]
   - 📋 Dictamen de Auditoría: [Explicación técnica detallada y recomendación final para la validación del Master]

FORMATO DE SALIDA (ESTRICTAMENTE JSON):
{
  "confidenceScore": number,
  "matchLevel": "ALTA" | "MEDIA" | "BAJA" | "NULA",
  "isRadiologistOrDoctor": boolean,
  "foundAffiliations": ["Hospital...", "Colegio..."],
  "structuredReport": "Texto del informe estructurado completo con los 4 apartados",
  "isValid": boolean
}`;

  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: "Eres un auditor clínico senior y verificador de credenciales médicas para plataformas sanitarias de alta criticidad.",
        responseMimeType: "application/json",
        temperature: 0.1,
        tools: [{ googleSearch: {} }] as any
      },
      contents: prompt
    });

    logUsage(res, modelId, true);
    const data = robustJSONParse(res.text || "{}");

    const confidenceScore = typeof data.confidenceScore === 'number' 
      ? Math.max(0, Math.min(100, Math.round(data.confidenceScore)))
      : (data.isValid ? 90 : 45);
    
    const isValid = confidenceScore >= 90;
    const matchLevel = data.matchLevel || (confidenceScore >= 90 ? 'ALTA' : confidenceScore >= 70 ? 'MEDIA' : confidenceScore >= 40 ? 'BAJA' : 'NULA');
    
    let reason = data.structuredReport || data.reason || "";
    if (!reason || reason.length < 20) {
      reason = `🎯 ÍNDICE DE CERTEZA: ${confidenceScore}% - Coincidencia ${matchLevel}\n🩺 CONDICIÓN RADIOLÓGICA/MÉDICA: ${data.isRadiologistOrDoctor ? 'Indicios compatibles con ejercicio médico/radiológico.' : 'Sin confirmación inequívoca de especialidad radiológica.'}\n🏛️ FUENTES Y AFILIACIONES: ${Array.isArray(data.foundAffiliations) && data.foundAffiliations.length > 0 ? data.foundAffiliations.join(', ') : 'Fuentes abiertas web / LinkedIn'}\n📋 DICTAMEN DE AUDITORÍA: El perfil ha obtenido una puntuación de certeza del ${confidenceScore}%. ${isValid ? 'Pre-aprobación técnica concedida (≥90%). Pendiente de firma manual del Master.' : 'No alcanza el umbral del 90%. Requiere revisión manual exhaustiva por parte del Master.'}`;
    }

    return {
      isValid,
      confidenceScore,
      matchLevel,
      isRadiologistOrDoctor: data.isRadiologistOrDoctor ?? (confidenceScore >= 70),
      foundAffiliations: Array.isArray(data.foundAffiliations) ? data.foundAffiliations : [],
      summaryConclusion: data.summaryConclusion || '',
      reason
    };
  } catch (err) {
    console.error("Error en verifyProfessionalIdentity:", err);
    return {
      isValid: false,
      confidenceScore: 0,
      matchLevel: 'NULA',
      isRadiologistOrDoctor: false,
      reason: "⚠️ No se ha podido completar la consulta con el motor de búsqueda y auditoría IA (gemini-3.5-flash). Por favor, verifique la conexión y vuelva a pulsar 'Re-Validar IA'.",
      foundAffiliations: []
    };
  }
};

export const fetchLatestGeminiPricing = async (): Promise<SystemPricing> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  const prompt = `Busca los precios oficiales actuales de la API de Google Gemini en ai.google.dev/pricing.
  Necesito los costos por cada 1 MILLÓN de tokens para:
  1. Gemini Flash (Input y Output).
  2. Gemini Pro (Input y Output).
  3. Costo fijo por cada consulta de Google Search Grounding.
  
  Devuelve estrictamente un objeto JSON con esta estructura exacta:
  {
    "flash": {"inputPer1M": numero, "outputPer1M": numero},
    "pro": {"inputPer1M": numero, "outputPer1M": numero},
    "searchGrounding": numero
  }`;

  try {
    const res = await ai.models.generateContent({ 
      model: modelId,
      config: { 
        responseMimeType: "application/json",
        temperature: 0.0,
        tools: [{ googleSearch: {} }]
      },
      contents: prompt
    });

    logUsage(res, modelId, true);
    const data = robustJSONParse(res.text || "{}");
    
    // Transformamos al formato interno del sistema
    return {
      models: {
        'gemini-3.1-flash-lite': { 
          input: (data.flash?.inputPer1M || 0.01) / 1000000, 
          output: (data.flash?.outputPer1M || 0.04) / 1000000, 
          label: 'Gemini Lite (8B)', 
          color: 'bg-cyan-500' 
        },
        'gemini-3.5-flash': { 
          input: (data.flash?.inputPer1M || 0.075) / 1000000, 
          output: (data.flash?.outputPer1M || 0.30) / 1000000, 
          label: 'Gemini 3.5 Flash', 
          color: 'bg-emerald-600' 
        },
        'gemini-1.5-pro': { 
          input: (data.pro?.inputPer1M || 1.25) / 1000000, 
          output: (data.pro?.outputPer1M || 5.00) / 1000000, 
          label: 'Gemini Pro', 
          color: 'bg-indigo-600' 
        },
        'gemini-2.5-pro': {
          input: 1.25 / 1000000,
          output: 5.00 / 1000000,
          label: 'Gemini 2.5 Pro',
          color: 'bg-indigo-700'
        },
        'gemini-2.5-flash': {
          input: (data.flash?.inputPer1M || 0.10) / 1000000,
          output: (data.flash?.outputPer1M || 0.40) / 1000000,
          label: 'Gemini 2.5 Flash',
          color: 'bg-amber-600'
        },
        'gemini-1.5-flash': {
          input: (data.flash?.inputPer1M || 0.10) / 1000000,
          output: (data.flash?.outputPer1M || 0.40) / 1000000,
          label: 'Gemini 1.5 Flash',
          color: 'bg-amber-500'
        }
      },
      searchFlatFee: data.searchGrounding || 0.035,
      lastUpdated: Date.now()
    };
  } catch (e) {
    throw new Error(formatGeminiError(e, "Búsqueda de Tarifas"));
  }
};

// --- GESTIÓN DE CONCURRENCIA Y REINTENTOS (ANTI-429) ---

/**
 * Cola de ejecución global para evitar saturar la API con peticiones concurrentes
 * que disparan el error 429.
 */
class GeminiRequestQueue {
  private queue: (() => Promise<any>)[] = [];
  private processing = false;
  private lastRequestTime = 0;
  private minInterval = 1000;
  private globalPauseUntil = 0;

  async add<T>(request: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await request();
          resolve(result);
        } catch (e: any) {
          const errorMsg = (e.message || "").toLowerCase();
          if (errorMsg.includes("429") || errorMsg.includes("quota")) {
            console.warn("[GeminiQueue] 429 detectado. Pausando cola global durante 5s.");
            this.globalPauseUntil = Date.now() + 5000;
          }
          reject(e);
        }
      });
      this.process();
    });
  }

  private async process() {
    if (this.processing || this.queue.length === 0) return;
    this.processing = true;

    while (this.queue.length > 0) {
      const now = Date.now();
      
      // Respetar pausa global por 429
      if (now < this.globalPauseUntil) {
        await new Promise(r => setTimeout(r, this.globalPauseUntil - now));
        continue;
      }

      // Respetar intervalo mínimo
      const wait = Math.max(0, this.minInterval - (now - this.lastRequestTime));
      if (wait > 0) await new Promise(r => setTimeout(r, wait));

      const nextRequest = this.queue.shift();
      if (nextRequest) {
        this.lastRequestTime = Date.now();
        await nextRequest();
      }
    }

    this.processing = false;
  }
}

const globalQueue = new GeminiRequestQueue();

/**
 * Ejecuta una petición a Gemini con reintentos inteligentes y backoff exponencial.
 */
async function callGeminiWithRetry<T>(
  operation: () => Promise<T>,
  context: string,
  maxAttempts: number = 5
): Promise<T> {
  let attempt = 0;
  while (attempt < maxAttempts) {
    try {
      return await operation();
    } catch (e: any) {
      attempt++;
      const errorMsg = (e.message || "").toLowerCase();
      const is429 = errorMsg.includes("429") || errorMsg.includes("quota") || errorMsg.includes("exhausted") || errorMsg.includes("rate_limit");
      
      if (attempt < maxAttempts && (is429 || errorMsg.includes("fetch") || errorMsg.includes("network") || errorMsg.includes("timeout"))) {
        // Backoff exponencial: 3s, 6s, 12s... pero más agresivo si es 429
        const delay = is429 ? (attempt * 3000) : (attempt * 1500);
        console.warn(`[Gemini Retry] Fallo en ${context} (Intento ${attempt}/${maxAttempts}). Reintentando en ${delay}ms por error intermitente...`, e);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      throw e;
    }
  }
  throw new Error(`Fallo crítico tras ${maxAttempts} intentos en ${context}. Por favor, reduce la frecuencia de envío.`);
}

// --- PROMPTS MAESTROS ---

const ELITE_PHD_PROMPT = `ERES UN CATEDRÁTICO RADIÓLOGO PhD DE ÉLITE ESPECIALISTA EN REFINAMIENTO MÉDICO DE ALTA PRECISIÓN.
Tu misión es pulir y organizar los hallazgos radiológicos con precisión clínica — NO es enriquecer ni embellecer el texto con más palabras.

PROTOCOLOS DE ORGANIZACIÓN:
1. JERARQUÍA CLÍNICA: Organiza el contenido de lo GENERAL a lo PARTICULAR, en el mismo orden sistemático/anatómico en que se exploran las estructuras.
2. ESTRUCTURA: Los hallazgos significativos deben estar integrados en la narrativa clínica principal, en el lugar anatómico que les corresponde — no los adelantes al inicio solo por importancia.

REGLAS DE ORO (MANDATORIAS):
1. ECONOMÍA DE PALABRAS (CRÍTICO): Cada frase debe aportar información clínica nueva. Elimina cualquier frase, calificativo o transición que no cambie el significado clínico. Prohibido añadir texto solo para sonar más elaborado.
2. IDENTIDAD: Redacta con precisión de doctorado, usando terminología médica estándar — NO vocabulario rimbombante ni artificialmente sofisticado.
3. PÁRRAFOS (CRÍTICO): Cada bloque de hallazgos o sección debe ser un párrafo independiente. Usa OBLIGATORIAMENTE un DOBLE SALTO DE LÍNEA REAL (\\n\\n) entre párrafos.
4. PROHIBICIÓN: Terminantemente prohibido el uso de Markdown (**), asteriscos, guiones al inicio de línea, viñetas o listas numeradas.
5. TÍTULOS: No agregues títulos innecesarios o redundantes. Solo prosa médica pura y fluida.
6. FORMATO: Solo usa HTML <b> para términos clave. El resto debe ser texto plano.
7. NATURALIDAD: El resultado debe sonar como un informe dictado por un radiólogo experto humano, conciso y directo — no como un texto académico inflado.`;

const BASIC_MEDICAL_PROMPT = `ERES UN ASISTENTE RADIOLÓGICO PROFESIONAL.
Tu tarea es organizar hallazgos radiológicos en un documento estructurado y coherente.
Usa lenguaje médico técnico preciso.
Separa el contenido por secciones: Técnica, Hallazgos y Conclusión.
Usa DOBLE SALTO DE LÍNEA (\\n\\n) entre cada sección para asegurar la legibilidad.
Mantén un tono profesional y directo.
Solo usa HTML básico (<b>) para resaltar términos clave.`;

const FAST_FUSION_PhD_PROMPT = `context:
  role: "Expert Clinical Radiologist and Text Editor"
  task: "Merge real-time medical dictation into a base report template. Output strictly in SPANISH."

inputs:
  base_template: "{{INYECTAR_AQUÍ_LA_PLANTILLA_BASE_O_DEJAR_VACÍO_SI_NO_EXISTE}}"
  doctor_dictation: "{{INYECTAR_AQUÍ_EL_DICTADO_DEL_MEDICO}}"

immutable_layout_rules:
  - "PARAGRAPH FIDELITY: Replicate the exact paragraph layout of the BASE_TEMPLATE. You are strictly forbidden from collapsing paragraphs. Use literal '\\\\n\\\\n' to separate blocks."
  - "CASING & TITLES SACREDNESS: Retain and respect the exact casing (uppercase/lowercase), titles, and subtitle headers of the BASE_TEMPLATE exactly as written without alterations. If the BASE_TEMPLATE contains a title or header, it MUST be preserved absolutely intact in the findings."
  - "PROHIBITION OF LISTS AND NUMBERS: You are STRICTLY FORBIDDEN from adding numbers (e.g., 1., 2.), dashes (-), asterisks (*), or bullet points to list findings or conclusions. Separate independent clinical ideas ONLY by creating a new clean paragraph with a double line break (\\\\n\\\\n)."

clinical_injection_protocol:
  - "Measurement Safeguard: Retain every single measurement, numerical value, and lateralidad (left/right) from the doctor_dictation. Do not round or omit them."
  - "Direct Replacement: Locate the paragraph in the BASE that matches the dictation context, delete the obsolete normal phrase, and insert the new finding."
  - "CLINICAL QUALITY OVER RIGIDITY: The BASE_TEMPLATE is a structural guide, not a stone mold. If a dictation finding negates, updates, or overlaps multiple sections of the base, consolidate them into a single elegant paragraph. YOU ARE STRICTLY FORBIDDEN from duplicating or repeating the same clinical finding across different paragraphs just to fill the template slots. Avoid circular redundancy."

terminology_correction_protocol:
  - "CONTEXTO ANATÓMICO PRIMERO: Antes de corregir cualquier escala, identifica de qué región anatómica o subespecialidad trata el resto del dictado/informe (columna, neuro/cerebro, tórax, abdomen, mama, articulaciones, tiroides, etc.). Prioriza y aplica únicamente escalas que correspondan a esa región — por ejemplo, si el informe es de columna lumbar, considera Pfirrmann, Modic o Kellgren-Lawrence, pero NO BI-RADS ni Bosniak, que no aplican a esa región. Nunca 'reconozcas' una escala de una subespecialidad que no coincide con el contexto anatómico del estudio actual, aunque suene fonéticamente parecida."
  - "CONOCIMIENTO AMPLIO: Tienes entrenamiento médico extenso sobre escalas y sistemas de clasificación radiológica de todas las subespecialidades. Si detectas en el dictado el nombre de una escala mal transcrito fonéticamente, usa tu conocimiento médico para corregirlo a su forma oficial correcta antes de integrarlo al informe final — no te limites a una lista cerrada de ejemplos."
  - "PATRÓN -RADS: Cualquier secuencia fonética tipo 'X rads' o 'X-rads' (bi rads, ti rads, pi rads, li rads, lung rads, o rads, c rads, ni rads, etc.) debe formatearse siempre como X-RADS en mayúsculas con guion, sea cual sea la letra X."
  - "PATRÓN APELLIDO+GRADO: Cuando el dictado combine un nombre propio con 'grado', 'estadio', 'score', 'clasificación' o 'escala' (ej. 'grado de Fazekas', 'estadio de Bosniak', 'score de Kellgren-Lawrence'), reconoce que se trata de una escala epónima y corrige el apellido a su ortografía médica correcta, no a como suena fonéticamente."
  - "EJEMPLOS DE REFERENCIA (no exhaustivos, solo para calibrar el criterio): Fazekas, ASPECTS, Fisher, Hunt-Hess, Spetzler-Martin, Kellgren-Lawrence, Modic, Pfirrmann, Salter-Harris, Fleischner, RECIST, Bosniak, Balthazar, Child-Pugh, Bethesda, BI-RADS, TI-RADS, PI-RADS, LI-RADS."

title_protocol:
  - "Este bloque SOLO se activa si el input incluye la línea 'TITULO_SOLICITADO: SI'. Si dice 'TITULO_SOLICITADO: NO' o no aparece, IGNORA por completo este bloque y genera 'findings' exactamente como lo harías normalmente, sin ningún título añadido."
  - "Si TITULO_SOLICITADO es SI: determina el título del estudio (Modalidad + Región anatómica, ej. 'RM CRANEAL', 'TC TÓRAX', 'RM COLUMNA LUMBAR'), priorizando el título/encabezado que ya exista en el BASE_TEMPLATE si lo hay. Si no existe o es demasiado largo/verboso, sintetízalo a su forma breve estándar (Modalidad + Región), sin perder precisión clínica."
  - "El título va en MAYÚSCULAS, como primera línea de 'findings', seguido de un salto de línea físico en blanco (\\n\\n) antes del resto del contenido de hallazgos."
  - "Cuando TITULO_SOLICITADO sea SI, este título de primera línea es la ÚNICA aparición del título/encabezado en todo el texto de 'findings' — no lo repitas ni lo dupliques en ningún otro punto del texto, incluso si otra regla de este prompt menciona preservar el título de la base en otro lugar. Esta instrucción tiene prioridad sobre esa regla únicamente para la colocación del título."

output_instructions:
  format: "Return EXCLUSIVELY a valid JSON object. No introductory text, no markdown code block markers."
  schema:
    reasoning_space: "Paso 1: Extraer y listar todas las medidas numéricas del dictado para anclaje de atención. Paso 2: Identificar qué párrafo de la base cambia. Paso 3: Planificar la salida asegurando la ausencia total de números de lista (1., 2.) o guiones."
    findings: "Texto del informe fusionado, estructurado únicamente en párrafos limpios de prosa fluida (en Español). Si la base tiene un título o encabezado, consérvalo intacto al inicio."
    conclusion: "Síntesis de los hallazgos ya presentes en el texto (base + dictado), en párrafos fluidos, separados estrictamente por \\\\n\\\\n. Debe derivarse exclusivamente de lo ya descrito — sin agregar interpretación diagnóstica, probabilidad, estadificación ni contenido no mencionado explícitamente. Sin números ni viñetas."
    differential: "Diagnósticos diferenciales en prosa continua u objeto vacío \\"\\"."
`;

const NORMAL_FUSION_PhD_PROMPT = `context:
  role: "Elite Neuroradiologist & Clinical Text Editor"
  task: "Merge clinical dictation into a base report template with absolute structural alignment and professional clinical coherence. Output strictly in SPANISH."

inputs:
  base_template: "{{INYECTAR_AQUÍ_LA_PLANTILLA_BASE_O_DEJAR_VACÍO_SI_NO_EXISTE}}"
  doctor_dictation: "{{INYECTAR_AQUÍ_EL_DICTADO_DEL_MEDICO}}"

immutable_layout_rules:
  - "POSITIONAL FIDELITY: Treat the BASE_TEMPLATE as a sacred, fixed visual layout. Keep the exact boundaries and sequence of the original paragraphs using literal '\\\\n\\\\n' escapes."
  - "CASING & TITLES SACREDNESS: Retain and respect the exact casing (uppercase/lowercase), titles, and subtitle headers of the BASE_TEMPLATE exactly as written without alterations. If the BASE_TEMPLATE contains a title or header, it MUST be preserved absolutely intact in the findings."
  - "ANTI-LIST MANDATE: Under no circumstances will you structure findings or conclusions using numbers (1., 2., 3.), alphabetical keys (a), b)), hyphens (-), or bullet points. Every distinct diagnostic thought or independent conclusion must be written as a pure, elegant paragraph of narrative prose separated ONLY by physical '\\\\n\\\\n'."

clinical_fusion_protocol:
  - "Semantic Contrast: Evaluate what content in the BASE is updated or negated by the real-time DICTATION. Eradicate normal or outdated phrases completely, weaving the new pathology and its dimensions seamlessly into the surrounding text."
  - "Measurement Integrity: Absolute prohibition of omitting, transforming, or dropping sizes, volumes, or key physical parameters from the dictation."
  - "NON-REITERATION MANDATE: Prioritize clinical quality over template replication. If a newly dictated finding impacts multiple anatomical levels (e.g., degenerative changes from L3 to L5), write it ONCE as a unified narrative. Do not repeat the same pathology level-by-level if it damages the professional reading flow. Outdated base sentences must be ruthlessly deleted, not patched."

terminology_correction_protocol:
  - "CONTEXTO ANATÓMICO PRIMERO: Antes de corregir cualquier escala, identifica de qué región anatómica o subespecialidad trata el resto del dictado/informe (columna, neuro/cerebro, tórax, abdomen, mama, articulaciones, tiroides, etc.). Prioriza y aplica únicamente escalas que correspondan a esa región — por ejemplo, si el informe es de columna lumbar, considera Pfirrmann, Modic o Kellgren-Lawrence, pero NO BI-RADS ni Bosniak, que no aplican a esa región. Nunca 'reconozcas' una escala de una subespecialidad que no coincide con el contexto anatómico del estudio actual, aunque suene fonéticamente parecida."
  - "CONOCIMIENTO AMPLIO: Tienes entrenamiento médico extenso sobre escalas y sistemas de clasificación radiológica de todas las subespecialidades. Si detectas en el dictado el nombre de una escala mal transcrito fonéticamente, usa tu conocimiento médico para corregirlo a su forma oficial correcta antes de integrarlo al informe final — no te limites a una lista cerrada de ejemplos."
  - "PATRÓN -RADS: Cualquier secuencia fonética tipo 'X rads' o 'X-rads' (bi rads, ti rads, pi rads, li rads, lung rads, o rads, c rads, ni rads, etc.) debe formatearse siempre como X-RADS en mayúsculas con guion, sea cual sea la letra X."
  - "PATRÓN APELLIDO+GRADO: Cuando el dictado combine un nombre propio con 'grado', 'estadio', 'score', 'clasificación' o 'escala' (ej. 'grado de Fazekas', 'estadio de Bosniak', 'score de Kellgren-Lawrence'), reconoce que se trata de una escala epónima y corrige el apellido a su ortografía médica correcta, no a como suena fonéticamente."
  - "EJEMPLOS DE REFERENCIA (no exhaustivos, solo para calibrar el criterio): Fazekas, ASPECTS, Fisher, Hunt-Hess, Spetzler-Martin, Kellgren-Lawrence, Modic, Pfirrmann, Salter-Harris, Fleischner, RECIST, Bosniak, Balthazar, Child-Pugh, Bethesda, BI-RADS, TI-RADS, PI-RADS, LI-RADS."

title_protocol:
  - "Este bloque SOLO se activa si el input incluye la línea 'TITULO_SOLICITADO: SI'. Si dice 'TITULO_SOLICITADO: NO' o no aparece, IGNORA por completo este bloque y genera 'findings' exactamente como lo harías normalmente, sin ningún título añadido."
  - "Si TITULO_SOLICITADO es SI: determina el título del estudio (Modalidad + Región anatómica, ej. 'RM CRANEAL', 'TC TÓRAX', 'RM COLUMNA LUMBAR'), priorizando el título/encabezado que ya exista en el BASE_TEMPLATE si lo hay. Si no existe o es demasiado largo/verboso, sintetízalo a su forma breve estándar (Modalidad + Región), sin perder precisión clínica."
  - "El título va en MAYÚSCULAS, como primera línea de 'findings', seguido de un salto de línea físico en blanco (\\n\\n) antes del resto del contenido de hallazgos."
  - "Cuando TITULO_SOLICITADO sea SI, este título de primera línea es la ÚNICA aparición del título/encabezado en todo el texto de 'findings' — no lo repitas ni lo dupliques en ningún otro punto del texto, incluso si otra regla de este prompt menciona preservar el título de la base en otro lugar. Esta instrucción tiene prioridad sobre esa regla únicamente para la colocación del título."

output_requirements:
  format: "Strict JSON object. No preambles, no conversational notes."
  schema:
    reasoning_space: "1. Extraer y anclar medidas del dictado. 2. Mapear contraste de hipótesis párrafo por párrafo entre BASE y DICTADO (identificar qué frases quedan obsoletas). 3. Asegurar la ausencia total de signos de numeración o viñetas en la planificación final."
    findings: "Informe final fusionado con mímica estructural implacable en párrafos de prosa limpia (en Español). El título o encabezado original de la base debe conservarse intacto al inicio."
    conclusion: "Síntesis de los hallazgos ya presentes en el texto (base + dictado), en párrafos fluidos, separados estrictamente por \\\\n\\\\n. Debe derivarse exclusivamente de lo ya descrito — sin agregar interpretación diagnóstica, probabilidad, estadificación ni contenido no mencionado explícitamente. Sin números ni viñetas."
    differential: "Diagnósticos diferenciales u objeto vacío \\"\\"."
`;

const FLEXIBLE_FAST_FUSION_PhD_PROMPT = `context:
  role: "Expert Clinical Radiologist and Text Editor"
  task: "Merge real-time medical dictation into a base report template. Output strictly in SPANISH."

inputs:
  base_template: "{{INYECTAR_AQUÍ_LA_PLANTILLA_BASE_O_DEJAR_VACÍO_SI_NO_EXISTE}}"
  doctor_dictation: "{{INYECTAR_AQUÍ_EL_DICTADO_DEL_MEDICO}}"

immutable_layout_rules:
  - "PARAGRAPH FIDELITY & EXCEPTIONS: Replicate the paragraph layout of the BASE_TEMPLATE ONLY when findings are normal. If there is a pathological finding, you are explicitly ordered to break this layout to prioritize clinical truth."
  - "CASING & TITLES: Retain headers unless a pathological finding renders them obsolete or requires them to be moved."
  - "PROHIBITION OF LISTS AND NUMBERS: You are STRICTLY FORBIDDEN from adding numbers (e.g., 1., 2.), dashes (-), asterisks (*), or bullet points to list findings or conclusions. Separate independent clinical ideas ONLY by creating a new clean paragraph with a double line break (\\\\n\\\\n)."

clinical_injection_protocol:
  - "PATHOLOGY TRIAGE & JERARQUIZACIÓN: If the dictation contains high-relevance findings (tumors, acute infarcts, hemorrhages, fractures, etc.), these MUST be positioned at the very beginning of the 'findings' section as the FIRST paragraph, breaking the standard template order. Jerarquiza colocando lo grave primero."
  - "SURGICAL ANTI-CONTRADICTION: If the dictation reports a pathology, you MUST delete ONLY the specific sentence or phrase of 'normality' that directly contradicts that finding (e.g., if there is a tumor, delete 'no se observan procesos expansivos')."
  - "PRESERVATION OF OTHER NORMALITIES: You are STRICTLY FORBIDDEN from wiping out or omitting other unrelated normal findings of the BASE_TEMPLATE. If the dictation does not mention the other anatomical structures (e.g., orbits, sinuses, bones), their normal descriptions in the template MUST remain completely intact. Do not over-delete. Edit with a scalpel, not an axe."
  - "Measurement Safeguard: Retain every single measurement, numerical value, and lateralidad from the doctor_dictation. Do not round or omit them."

terminology_correction_protocol:
  - "CONTEXTO ANATÓMICO PRIMERO: Antes de corregir cualquier escala, identifica de qué región anatómica o subespecialidad trata el resto del dictado/informe (columna, neuro/cerebro, tórax, abdomen, mama, articulaciones, tiroides, etc.). Prioriza y aplica únicamente escalas que correspondan a esa región — por ejemplo, si el informe es de columna lumbar, considera Pfirrmann, Modic o Kellgren-Lawrence, pero NO BI-RADS ni Bosniak, que no aplican a esa región. Nunca 'reconozcas' una escala de una subespecialidad que no coincide con el contexto anatómico del estudio actual, aunque suene fonéticamente parecida."
  - "CONOCIMIENTO AMPLIO: Tienes entrenamiento médico extenso sobre escalas y sistemas de clasificación radiológica de todas las subespecialidades. Si detectas en el dictado el nombre de una escala mal transcrito fonéticamente, usa tu conocimiento médico para corregirlo a su forma oficial correcta antes de integrarlo al informe final — no te limites a una lista cerrada de ejemplos."
  - "PATRÓN -RADS: Cualquier secuencia fonética tipo 'X rads' o 'X-rads' (bi rads, ti rads, pi rads, li rads, lung rads, o rads, c rads, ni rads, etc.) debe formatearse siempre como X-RADS en mayúsculas con guion, sea cual sea la letra X."
  - "PATRÓN APELLIDO+GRADO: Cuando el dictado combine un nombre propio con 'grado', 'estadio', 'score', 'clasificación' o 'escala' (ej. 'grado de Fazekas', 'estadio de Bosniak', 'score de Kellgren-Lawrence'), reconoce que se trata de una escala epónima y corrige el apellido a su ortografía médica correcta, no a como suena fonéticamente."
  - "EJEMPLOS DE REFERENCIA (no exhaustivos, solo para calibrar el criterio): Fazekas, ASPECTS, Fisher, Hunt-Hess, Spetzler-Martin, Kellgren-Lawrence, Modic, Pfirrmann, Salter-Harris, Fleischner, RECIST, Bosniak, Balthazar, Child-Pugh, Bethesda, BI-RADS, TI-RADS, PI-RADS, LI-RADS."

title_protocol:
  - "Este bloque SOLO se activa si el input incluye la línea 'TITULO_SOLICITADO: SI'. Si dice 'TITULO_SOLICITADO: NO' o no aparece, IGNORA por completo este bloque y genera 'findings' exactamente como lo harías normalmente, sin ningún título añadido."
  - "Si TITULO_SOLICITADO es SI: determina el título del estudio (Modalidad + Región anatómica, ej. 'RM CRANEAL', 'TC TÓRAX', 'RM COLUMNA LUMBAR'), priorizando el título/encabezado que ya exista en el BASE_TEMPLATE si lo hay. Si no existe o es demasiado largo/verboso, sintetízalo a su forma breve estándar (Modalidad + Región), sin perder precisión clínica."
  - "El título va en MAYÚSCULAS, como primera línea de 'findings', seguido de un salto de línea físico en blanco (\\n\\n) antes del resto del contenido de hallazgos."
  - "Cuando TITULO_SOLICITADO sea SI, este título de primera línea es la ÚNICA aparición del título/encabezado en todo el texto de 'findings' — no lo repitas ni lo dupliques en ningún otro punto del texto, incluso si otra regla de este prompt menciona preservar el título de la base en otro lugar. Esta instrucción tiene prioridad sobre esa regla únicamente para la colocación del título."

output_instructions:
  format: "Return EXCLUSIVELY a valid JSON object. No introductory text, no markdown code block markers. Return raw JSON string starting with { and ending with }."
  schema:
    reasoning_space: "Paso 1: Extraer medidas del dictado. Paso 2: Identificar hallazgos críticos para moverlos al primer párrafo (Jerarquía Crítica). Paso 3: Identificar qué frase exacta de normalidad se contradice y debe ser eliminada (Bisturí Semántico). Paso 4: Listar qué secciones normales de la base NO se tocan y se deben conservar intactas."
    findings: "Texto del informe fusionado, estructurado únicamente en párrafos limpios de prosa fluida (en Español). Sin contradicciones. Lo patológico crítico va al inicio."
    conclusion: "Síntesis de los hallazgos ya presentes en el texto (base + dictado), en párrafos fluidos, separados estrictamente por \\\\n\\\\n. Debe derivarse exclusivamente de lo ya descrito — sin agregar interpretación diagnóstica, probabilidad, estadificación ni contenido no mencionado explícitamente. Sin números ni viñetas."
    differential: "Diagnósticos diferenciales en prosa continua u objeto vacío \\"\\"."
`;

const FLEXIBLE_NORMAL_FUSION_PhD_PROMPT = `context:
  role: "Elite Neuroradiologist & Clinical Text Editor"
  task: "Merge clinical dictation into a base report template with absolute structural alignment and professional clinical coherence. Output strictly in SPANISH."

inputs:
  base_template: "{{INYECTAR_AQUÍ_LA_PLANTILLA_BASE_O_DEJAR_VACÍO_SI_NO_EXISTE}}"
  doctor_dictation: "{{INYECTAR_AQUÍ_EL_DICTADO_DEL_MEDICO}}"

immutable_layout_rules:
  - "DYNAMIC POSITIONAL FIDELITY: The BASE_TEMPLATE is a structural guide, NOT a rigid prison. You must break the sequence and layout if a critical finding requires clinical hierarchy or to prevent logical contradictions."
  - "ANTI-LIST MANDATE: Under no circumstances will you structure findings or conclusions using numbers (1., 2., 3.), alphabetical keys (a), b)), hyphens (-), or bullet points. Every distinct diagnostic thought or independent conclusion must be written as a pure, elegant paragraph of narrative prose separated ONLY by physical '\\\\n\\\\n'."

clinical_fusion_protocol:
  - "CLINICAL JERARQUIZACIÓN (CRITICAL FIRST): Relevant findings (such as infarcts, tumors, hemorrhages, fractures, acute lesions) possess absolute hierarchy. You must break the standard anatomical order of the template to place these critical findings in the FIRST PARAGRAPH of the findings section, ensuring they capture immediate attention."
  - "SURGICAL CONTRADICTION ERADICATION: Evaluate what specific text in the BASE is logically negated by the DICTATION. You must surgically extract and delete ONLY the phrases of false normality that clash with the new pathology (e.g., replace 'No intracranial masses' with the tumor details). A report cannot contain a normal statement and a pathological finding for the same system."
  - "CONSERVATION MANDATE FOR UNRELATED SECTIONS: Non-contradictory normal descriptions in the BASE_TEMPLATE (e.g., normal vasculature, normal sinuses, normal bones) must be preserved absolutely intact if the dictation doesn't contradict them. Do not cause a blank-out of the rest of the template; edit with a scalpel, not an axe."
  - "NON-REITERATION MANDATE: Prioritize clinical quality over template replication. If a newly dictated finding impacts multiple anatomical levels (e.g., degenerative changes from L3 to L5), write it ONCE as a unified narrative. Outdated base sentences must be ruthlessly deleted, not patched."
  - "Measurement Integrity: Absolute prohibition of omitting, transforming, or dropping sizes, volumes, or key physical parameters from the dictation."

terminology_correction_protocol:
  - "CONTEXTO ANATÓMICO PRIMERO: Antes de corregir cualquier escala, identifica de qué región anatómica o subespecialidad trata el resto del dictado/informe (columna, neuro/cerebro, tórax, abdomen, mama, articulaciones, tiroides, etc.). Prioriza y aplica únicamente escalas que correspondan a esa región — por ejemplo, si el informe es de columna lumbar, considera Pfirrmann, Modic o Kellgren-Lawrence, pero NO BI-RADS ni Bosniak, que no aplican a esa región. Nunca 'reconozcas' una escala de una subespecialidad que no coincide con el contexto anatómico del estudio actual, aunque suene fonéticamente parecida."
  - "CONOCIMIENTO AMPLIO: Tienes entrenamiento médico extenso sobre escalas y sistemas de clasificación radiológica de todas las subespecialidades. Si detectas en el dictado el nombre de una escala mal transcrito fonéticamente, usa tu conocimiento médico para corregirlo a su forma oficial correcta antes de integrarlo al informe final — no te limites a una lista cerrada de ejemplos."
  - "PATRÓN -RADS: Cualquier secuencia fonética tipo 'X rads' o 'X-rads' (bi rads, ti rads, pi rads, li rads, lung rads, o rads, c rads, ni rads, etc.) debe formatearse siempre como X-RADS en mayúsculas con guion, sea cual sea la letra X."
  - "PATRÓN APELLIDO+GRADO: Cuando el dictado combine un nombre propio con 'grado', 'estadio', 'score', 'clasificación' o 'escala' (ej. 'grado de Fazekas', 'estadio de Bosniak', 'score de Kellgren-Lawrence'), reconoce que se trata de una escala epónima y corrige el apellido a su ortografía médica correcta, no a como suena fonéticamente."
  - "EJEMPLOS DE REFERENCIA (no exhaustivos, solo para calibrar el criterio): Fazekas, ASPECTS, Fisher, Hunt-Hess, Spetzler-Martin, Kellgren-Lawrence, Modic, Pfirrmann, Salter-Harris, Fleischner, RECIST, Bosniak, Balthazar, Child-Pugh, Bethesda, BI-RADS, TI-RADS, PI-RADS, LI-RADS."

title_protocol:
  - "Este bloque SOLO se activa si el input incluye la línea 'TITULO_SOLICITADO: SI'. Si dice 'TITULO_SOLICITADO: NO' o no aparece, IGNORA por completo este bloque y genera 'findings' exactamente como lo harías normalmente, sin ningún título añadido."
  - "Si TITULO_SOLICITADO es SI: determina el título del estudio (Modalidad + Región anatómica, ej. 'RM CRANEAL', 'TC TÓRAX', 'RM COLUMNA LUMBAR'), priorizando el título/encabezado que ya exista en el BASE_TEMPLATE si lo hay. Si no existe o es demasiado largo/verboso, sintetízalo a su forma breve estándar (Modalidad + Región), sin perder precisión clínica."
  - "El título va en MAYÚSCULAS, como primera línea de 'findings', seguido de un salto de línea físico en blanco (\\n\\n) antes del resto del contenido de hallazgos."
  - "Cuando TITULO_SOLICITADO sea SI, este título de primera línea es la ÚNICA aparición del título/encabezado en todo el texto de 'findings' — no lo repitas ni lo dupliques en ningún otro punto del texto, incluso si otra regla de este prompt menciona preservar el título de la base en otro lugar. Esta instrucción tiene prioridad sobre esa regla únicamente para la colocación del título."

output_requirements:
  format: "Strict JSON object. No preambles, no conversational notes. Return raw JSON string starting with { and ending with }."
  schema:
    reasoning_space: "1. Extraer y anclar medidas. 2. Identificar contradicciones lógicas directas o indirectas entre la BASE y el DICTADO (eliminar frases de falsa normalidad). 3. Aplicar Triaje Clínico: evaluar si hay hallazgos mayores que requieran romper el orden de la plantilla para ser posicionados en primer lugar. 4. Listar las secciones de normalidad que se deben mantener intactas."
    findings: "Informe final fusionado con jerarquización clínica implacable. Párrafos de prosa limpia (en Español). Las patologías críticas abren el reporte. El título original se conserva si procede, adaptando el inicio a la urgencia médica."
    conclusion: "Síntesis de los hallazgos ya presentes en el texto (base + dictado), en párrafos fluidos, separados estrictamente por \\\\n\\\\n. Debe derivarse exclusivamente de lo ya descrito — sin agregar interpretación diagnóstica, probabilidad, estadificación ni contenido no mencionado explícitamente. Sin números ni viñetas."
    differential: "Diagnósticos diferenciales u objeto vacío \\"\\"."
`;




export const sendChatMessageStream = async (history: GeminiContent[], newMessage: string, modelName: 'gemini-3.1-flash-lite' | 'gemini-3.1-flash' | 'gemini-1.5-pro' | 'gemini-2.5-pro' = 'gemini-3.1-flash-lite') => {
  const ai = getAIClient();
  const targetModel = modelName === 'gemini-3.1-flash' 
    ? 'gemini-3.5-flash' 
    : modelName === 'gemini-1.5-pro' || modelName === 'gemini-2.5-pro'
      ? 'gemini-3.5-flash'
      : modelName;
  
  const sysInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD. 
     FORMATO REQUERIDO: Solo usa HTML básico (<b>). Prohibido Markdown (**), guiones (---) o almohadillas (#).
     REGLA DE SEPARACIÓN: Usa saltos de línea físicos (\n\n) para separar conceptos y párrafos.`;

  const cleanContents = [
    ...history.slice(-10).map(h => ({
      role: h.role === 'model' ? 'model' : 'user', 
      parts: h.parts.map(p => ({ text: p.text || "" }))
    })),
    { role: 'user', parts: [{ text: newMessage }] }
  ];

  try {
    // Los streams no se pueden encolar fácilmente de forma estricta, pero callGeminiWithRetry ayuda con el handshake
    return await ai.models.generateContentStream({
      model: targetModel,
      contents: cleanContents as any,
      config: {
        systemInstruction: sysInstruction,
        safetySettings: SAFETY_SETTINGS,
        temperature: 0.1,
        tools: [{ googleSearch: {} }] 
      }
    });
  } catch (error: any) { 
    throw new Error(formatGeminiError(error, `Chat`)); 
  }
};

export const generateRadIA2Response = async (text: string, reports: ReportTemplate[], config: any): Promise<any> => {
  const ai = getAIClient();
  const startTime = Date.now();
  const modelId = 'gemini-3.1-flash-lite';
  
  const systemInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD. 
  Tu tarea es procesar un dictado y generar un informe estructurado en JSON.
  
  EXTENSIÓN: ${config.extension || 'Estándar'}
  ESTILO: Profesionál PhD.
  
  CONFIGURACIÓN:
  - Técnica: ${config.technique ? 'Requerido' : 'No requerido'}
  - Conclusión: ${config.conclusion ? 'Requerido' : 'No requerido'}
  - Diferencial: ${config.differential ? 'Requerido' : 'No requerido'}
  
  JSON OUTPUT SCHEMA:
  {
    "findings": "hallazgos detallados",
    "conclusion": "conclusión diagnóstica",
    "differential": "diagnósticos diferenciales o cadena vacía"
  }`;

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: modelId,
        config: {
          systemInstruction,
          responseMimeType: "application/json",
          temperature: 0.1
        },
        contents: `DICTADO: ${text}`
      });
      
      logUsage(res, modelId);
      const data = robustJSONParse(res.text || "{}");
      
      const result = {
        findings: cleanAIResponse(data.findings || text),
        conclusion: cleanAIResponse(data.conclusion || ""),
        differential: cleanAIResponse(data.differential || "")
      };

      await logLatency(LatencyModule.REPORT_GEN, startTime, 'SUCCESS', null, text.length, { model: modelId, mode: 'radia2' });
      return result;
    }, "RadIA 2 Processing"));
  } catch (e) {
    await logLatency(LatencyModule.REPORT_GEN, startTime, 'ERROR', e, text.length, { model: modelId });
    throw new Error(formatGeminiError(e, "RadIA 2"));
  }
};

export const analyzeSuggestionsWithAI = async (suggestions: AppSuggestion[]): Promise<string> => {
  const ai = getAIClient();
  const modelId = "gemini-3.1-flash-lite";
  const prompt = `Analiza las siguientes sugerencias de usuarios de la aplicación MODULAR (Radiology AI Assistant). 
  Identifica los puntos más urgentes, las quejas comunes y las ideas más innovadoras. 
  Presenta un resumen ejecutivo estructurado para el administrador (Radiology Master).
  
  Sugerencias:
  ${suggestions.map(s => `- [${s.userName}]: ${s.text}`).join('\n')}
  `;

  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: "Eres un analista de producto experto en software médico y experiencia de usuario. Tu tono es profesional, ejecutivo y enfocado a la acción.",
        temperature: 0.2
      },
      contents: prompt
    });

    logUsage(res, modelId);
    return res.text || "No se pudo generar el análisis.";
  } catch (error) {
    console.error("AI Analysis error:", error);
    return "Error al conectar con el servicio de IA para el análisis.";
  }
};

export const analyzeVoiceCalibration = async (base64: string, mimeType: string): Promise<string> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: "Eres un experto en fonética y acústica médica. Analiza la voz del usuario y devuelve una descripción técnica MUY BREVE de su estilo de dictado (ej: 'Voz pausada, acento andaluz, énfasis en términos complejos').",
        temperature: 0.0
      },
      contents: [
        { inlineData: { mimeType, data: base64 } },
        "Analiza el perfil de voz del usuario."
      ]
    });
    logUsage(res, modelId);
    return res.text || "Perfil Estándar";
  } catch {
    return "Perfil Estándar";
  }
};

export const generateFindingsSummary = async (text: string): Promise<string> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: "Eres un radiólogo PhD. Resume los hallazgos proporcionados en una única frase concisa y técnica.",
        temperature: 0.1
      },
      contents: `Hallazgos: ${text}`
    });
    logUsage(res, modelId);
    return res.text || "Sin hallazgos significativos.";
  } catch { return "Error al resumir."; }
};

const PROMPT_MIXTO_MAESTRO = `context:
  role: "Elite Radiological Transcriptor & Linguistic Editor"
  task: "Process, clean, and orthographically correct real-time medical dictation (STT). Output strictly in SPANISH."

medical_vocabulary_anchor:
  neuro: "parénquima, isoseñal, hipointenso, hiperintenso, realce, gadolinio, restriction, difusión, sulcus, cisura, proceso expansivo, microangiopatía, desmielinizante"
  osteomuscular: "osteofitos, espondiloartrosis, protrusión, extrusión, listesis, receso lateral, foramen, uncoartrosis, pinzamiento, discopatía, deshidratación"
  general: "adenopatías, realce en anillo, linfoadenopatía, hepatoesplenomegalia, atelectasia, consolidación, derrame, masa tumoral, vena cava"
  
scale_recognition_protocol:
  - "CONTEXTO ANATÓMICO PRIMERO: Antes de corregir cualquier escala, identifica de qué región anatómica o subespecialidad trata el resto del dictado (columna, neuro/cerebro, tórax, abdomen, mama, articulaciones, tiroides, etc.). Prioriza y aplica únicamente escalas que correspondan a esa región — por ejemplo, si el dictado es de columna lumbar, considera Pfirrmann, Modic o Kellgren-Lawrence, pero NO BI-RADS ni Bosniak, que no aplican a esa región. Nunca 'reconozcas' una escala de una subespecialidad que no coincide con el contexto anatómico del estudio actual, aunque suene fonéticamente parecida."
  - "CONOCIMIENTO AMPLIO: Tienes entrenamiento médico extenso sobre escalas y sistemas de clasificación radiológica de todas las subespecialidades. Si el audio contiene algo que suena fonéticamente a una escala o sistema de clasificación radiológica, NO lo transcribas letra por letra tal como suena — usa tu conocimiento médico para identificar cuál es la escala real y escríbela en su forma oficial correcta, aunque no aparezca en los ejemplos de abajo."
  - "PATRÓN -RADS: Cualquier secuencia fonética tipo 'X rads' o 'X-rads' (bi rads, ti rads, pi rads, li rads, lung rads, o rads, c rads, ni rads, etc.) debe formatearse siempre como X-RADS en mayúsculas con guion, sea cual sea la letra X."
  - "PATRÓN APELLIDO+GRADO: Cuando el dictado combine un nombre propio con 'grado', 'estadio', 'score', 'clasificación' o 'escala' (ej. 'grado de Fazekas', 'estadio de Bosniak', 'score de Kellgren-Lawrence'), reconoce que se trata de una escala epónima y corrige el apellido a su ortografía médica correcta, no a como suena fonéticamente."
  - "EJEMPLOS DE REFERENCIA (no exhaustivos, solo para calibrar el criterio): Fazekas, ASPECTS, Fisher, Hunt-Hess, Spetzler-Martin, Kellgren-Lawrence, Modic, Pfirrmann, Salter-Harris, Fleischner, RECIST, Bosniak, Balthazar, Child-Pugh, Bethesda, BI-RADS, TI-RADS, PI-RADS, LI-RADS."

context_priority_rules:
  - "ANATOMICAL DOMINANCE: When the context involves abdominal, retroperitoneal, or urological structures (e.g., kidney, ureter, renal pelvis), the term 'SENO RENAL' has absolute priority over 'seno venoso'. If the STT captures phonetically similar terms, you MUST prioritize the renal anatomy terminology."
  - "CONTEXTUAL ANCHORING: Always validate the anatomical region before finalizing a term. If the dictation mentions 'masa tumoral', 'riñón', or 'vena cava' in the same phrase, assume the anatomy is renal."
  - "PHONETIC CORRECTION RULE: If the STT input contains words that sound similar to radiological terms (e.g., 'hizo señal' -> 'isoseñal', 'osteo fito' -> 'osteofitos'), you MUST strictly correct them to the formal medical term."

immutable_formatting_rules:
  - "ANATOMICAL LEVEL STANDARDIZATION: Every time a sequential anatomical or vertebral level is mentioned (e.g., 'l1 l2', 'l4 l5'), format it strictly in uppercase and separated by a hyphen: 'L1-L2', 'L4-L5'. Keep lateralities attached (e.g., 'L4-L5 derecho')."
  - "INTELLIGENT STRUCTURING: If the input text is a continuous unstructured block, segment it into logical paragraphs separated ONLY by a double real line break (\\\\n\\\\n)."
  - "VOICE COMMAND EXECUTION: Explicit voice commands like 'nueva línea', 'punto aparte', or 'nuevo párrafo' must be executed physically, generating real line breaks."

strict_literal_protocol:
  - "CLINICAL LITERALITY: If [MODE == GM1], you are STRICTLY FORBIDDEN from altering, summarizing, or replacing clinical terms with common synonyms. If the doctor dictates 'extrusión', under NO circumstance change it to 'protrusión'. Mime every literal detail.
     If [MODE == PHD], you are permitted to optimize or normalize technical prefixes under clinical supervision, retaining technical exactness but allowing academic consistency."
  - "NEGATION & MEASUREMENT SAFEGUARD: Absolute attention to negative particles ('no', 'sin', 'ausencia de') and numerical values. Do not alter the clinical meaning."
  - "EDITING_TOLERANCE: 
      IF [MODE == GM1]: STRICT_LITERAL_ONLY. Do not change any clinical term. If the dictation is colloquial, keep it colloquial. Do not optimize terminology.
      IF [MODE == PHD]: CLINICAL_NORMALIZATION_ALLOWED. You are permitted to normalize terms to their formal radiological standard (e.g., 'polilobulada' -> 'multilobulada') to ensure report consistency, BUT never alter the anatomical facts, measurements, or negations."

output_instructions:
  format: "Return EXCLUSIVELY the final processed, clean radiological text. No preambles, no conversational notes, no markdown code blocks, no HTML."
  clausula_de_silencio: "If the input text is empty or lacks intelligible medical info, return an absolute empty string (zero characters)."`;

export const transcribeAudio = async (audioSource: string | Blob, mimeType: string, modelNameOrFlexible: string | boolean = 'phd'): Promise<string> => {
  if (!audioSource) return ""; 

  let finalBase64: string;
  let finalMimeType = mimeType;

  if (audioSource instanceof Blob) {
    // Check if audio is empty or entirely silent (handled in AudioRecorder, but safe check here)
    if (audioSource.size === 0) {
      console.warn("transcribeAudio: Audio vacío detectado. Omitiendo llamada a la API.");
      return "";
    }
    
    const duration = await getAudioDuration(audioSource);
    if (duration < 0.5) { // Very short audios are usually noise/accidental
       console.warn("transcribeAudio: Audio demasiado corto (<0.5s). Omitiendo.");
       return "";
    }

    // ELIMINADA REGLA DE 30s PARA WAV. Siempre usamos el formato comprimido del grabador.
    finalBase64 = await blobToBase64(audioSource);
    finalMimeType = audioSource.type || mimeType;
  } else {
    finalBase64 = audioSource;
  }
  
  const ai = getAIClient();
  const startTime = Date.now();
  const payloadSize = finalBase64.length;
  
  const targetModel = 'gemini-3.5-flash-lite'; 
  
  const isFlexibleMode = typeof modelNameOrFlexible === 'boolean'
    ? modelNameOrFlexible
    : (modelNameOrFlexible === 'phd' || modelNameOrFlexible === 'flexible' || modelNameOrFlexible === 'phd_master');

  const mode = isFlexibleMode ? "PHD" : "GM1";
  const temperature = isFlexibleMode ? 0.3 : 0.0;
  
  const dynamicInstruction = `${PROMPT_MIXTO_MAESTRO}\n\n[MODE_SELECTION]: You are operating strictly in MODE: ${mode}. Follow the EDITING_TOLERANCE and CLINICAL LITERALITY rules mapped for this mode.`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000); // Increased slightly for safety but keeping it tight

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      // Dynamic MIME Type check for Safari fallback
      // audioSource.type is already handled above if it's a blob.
      
      const res = await ai.models.generateContent({
        model: targetModel,
        config: {
          systemInstruction: dynamicInstruction,
          safetySettings: SAFETY_SETTINGS,
          temperature: temperature,
          thinkingConfig: {
            thinkingLevel: ThinkingLevel.MINIMAL
          }
        },
        contents: [
          {
            parts: [
              { inlineData: { mimeType: finalMimeType, data: finalBase64 } }
            ]
          }
        ]
      }, { signal: controller.signal } as any);
      
      logUsage(res, targetModel);
      await logLatency(LatencyModule.TRANSCRIPTION, startTime, 'SUCCESS', null, payloadSize, { model: targetModel, attempt: 1, mimeType: finalMimeType });
      
      const resultText = res.text || "";
      if (!resultText.trim()) return "";
      
      const processedCommands = resultText;
      return normalizeMedicalText(processedCommands);
    }, "Transcripción Médica"));
  } catch (e: any) { 
    clearTimeout(timeoutId);
    await logLatency(LatencyModule.TRANSCRIPTION, startTime, 'ERROR', e, payloadSize, { model: targetModel });
    throw new Error(formatGeminiError(e, "Transcripción Médica"));
  } finally {
    clearTimeout(timeoutId);
  }
};

export const rateRadiologyReport = async (text: string, engineMode: string = 'phd'): Promise<any> => {
  const userId = auth.currentUser?.uid || 'guest_dev_offline';
  
  const isPHD = engineMode === 'phd' || engineMode === 'flexible' || engineMode === 'phd_master';
  const modeLabel = isPHD ? 'PHD' : 'GM1';

  // Semaphore: Evitar auditorías duplicadas para exactamente el mismo texto y modo
  const textHash = text.length + "_" + text.substring(0, 15);
  const lockId = `audit_${modeLabel}_${textHash}`;
  
  const hasLock = await acquireGlobalLock(userId, lockId, 5000);
  if (!hasLock) {
    console.log("[GeminiService] Auditoría omitida por semáforo global.");
    return { rating: 0, recommendations: "Sincronizando resultado..." };
  }

  const ai = getAIClient();
  const targetModel = 'gemini-3.8-flash';

  const systemInstruction = isPHD 
    ? `ERES UN REVISOR RADIÓLOGO SENIOR PhD DE ÉLITE (MODO PHD: EXCELENCIA Y NORMALIZACIÓN ACADÉMICA).
Tu función es realizar una AUDITORÍA TÉCNICA exhaustiva de informes radiológicos.

CRITERIOS DE EVALUACIÓN (MODO PHD):
1. PRECISIÓN TÉCNICA Y NORMALIZACIÓN: Evalúa la exactitud y elegancia de la terminología médica estándar y descripciones anatómicas.
2. DETECCIÓN DE ERRORES: Señala inconsistencias clínicas, omisiones críticas o discordancias de lateralidad y medidas.
3. EXCELENCIA EN REDACCIÓN: Sugiere cómo hacer el informe más conciso, directo y académico (nivel doctorado), optimizando el flujo sintáctico.
4. ORIENTACIÓN CLÍNICA: Proporciona recomendaciones dirigidas al médico prescriptor (pruebas complementarias o seguimiento) si el cuadro lo amerita.

PUNTUACIÓN:
Asigna de 1 a 5 estrellas basado en el rigor científico y claridad (5 = Excelencia Académica).`
    : `ERES UN REVISOR RADIÓLOGO SENIOR DE CALIDAD CLÍNICA (MODO GM1: LITERALIDAD ESTRICTA Y SEGURIDAD).
Tu función es auditar el informe priorizando la FIDELIDAD al dictado del radiólogo y la SEGURIDAD clínica.

CRITERIOS DE EVALUACIÓN (MODO GM1):
1. LITERALIDAD Y NO ALTERACIÓN: Respeta estrictamente los términos elegidos por el facultativo. ESTÁ PROHIBIDO sugerir sustitución de sinónimos si el término dictado es clínicamente válido.
2. SEGURIDAD CLÍNICA Y COHERENCIA: Detecta contradicciones diagnósticas internas, incoherencias entre descripción y conclusión, o errores de lateralidad/unidades métricas.
3. COMPLETITUD: Identifica únicamente si hay descripciones truncas o datos críticos ausentes según la técnica referida.

PUNTUACIÓN:
Asigna de 1 a 5 estrellas basado en coherencia interna y seguridad del informe literal (5 = Informe seguro y sin contradicciones).`;

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: targetModel,
        config: {
          systemInstruction,
          safetySettings: SAFETY_SETTINGS,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              rating: {
                type: Type.INTEGER,
                description: "Puntuación de 1 a 5 estrellas otorgada al informe."
              },
              recommendations: {
                type: Type.STRING,
                description: "Observaciones técnicas, detección de inconsistencias y sugerencias clínicas."
              }
            },
            required: ["rating", "recommendations"]
          },
          temperature: 0.1
        },
        contents: `EVALÚA EL SIGUIENTE INFORME EN MODO [${modeLabel}]:\n\n${text}`
      });

      logUsage(res, targetModel);
      return robustJSONParse(res.text || '{"rating":0, "recommendations":"Error de análisis"}');
    }, "Auditoría Médica"));
  } catch (e) { 
    throw new Error(formatGeminiError(e, "Auditoría Médica"));
  }
};

export const processAIDrafting = async (text: string, recommendations: string, config: any, engineMode: string = 'phd'): Promise<any> => {
  const ai = getAIClient();
  const startTime = Date.now();
  const payloadSize = text.length + recommendations.length;
  
  // Selección de modelo: Potenciado por 3.5 Flash
  let targetModel = 'gemini-3.8-flash';
  
  // 1. Construcción dinámica del contrato de propiedades según los checkboxes de la UI
  const properties: any = {};
  const requiredFields: string[] = [];

  if (config.technique) {
    properties.technique = {
      type: Type.STRING,
      description: "Técnica y protocolo de adquisición radiológica utilizado. Inalterado."
    };
    requiredFields.push("technique");
  }

  if (config.findings) {
    properties.findings = {
      type: Type.STRING,
      description: "Hallazgos detallados. OBLIGATORIO: Segmentar el texto aplicando de forma autónoma doble salto de línea físico (\\n\\n) cada 3 o 4 líneas o al cambiar de estructura anatómica. CRITERIO CLÍNICO: La calidad radiológica prima sobre la rigidez de la plantilla base; se prohíbe terminantemente duplicar o reiterar un mismo hallazgo en múltiples párrafos para forzar el encaje. Usa exclusivamente etiquetas HTML <b> para destacar diagnósticos relevantes. Prohibidos los asteriscos (*)."
    };
    requiredFields.push("findings");
  }

  if (config.conclusion) {
    properties.conclusion = {
      type: Type.STRING,
      description: "Resumen de hallazgos principales preciso derivado del dictado. Redactado en un párrafo limpio e independiente."
    };
    requiredFields.push("conclusion");
  }

  const systemInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD ESPECIALISTA EN REDACCIÓN DE INFORMES DE ALTA COMPLEJIDAD.
        Tu misión es transformar el BORRADOR en un informe final de excelencia médica.
        
        REGLAS DE ORO:
        1. IDENTIDAD: Redacta con el rigor de un doctorado, usando terminología clínica precisa y elegante.
        2. ESTRUCTURA JSON: Separa técnica, findings y conclusion (que actuará como Resumen de Hallazgos Principales) en sus respectivos campos. NO entregues diagnósticos diferenciales; no dejes ni generes diferenciales de ningún tipo.
        3. PÁRRAFOS (CRÍTICO): Cada bloque lógico de hallazgos debe ser un párrafo independiente. Usa DOBLE SALTO DE LÍNEA REAL (\\n\\n) entre párrafos.
        4. AUDITORÍA: Si se proporciona una SECCIÓN DE SUGERENCIAS, intégralas críticamente para elevar la calidad del informe final.
        5. FORMATO PRO: Solo usa HTML <b> para términos clave. PROHIBIDO el uso de Markdown (**).
        6. NO LISTAS: Redacta en prosa fluida, sin viñetas ni guiones.
        
        7. SEGMENTACIÓN GRAMATICAL Y JERARQUÍA ANATÓMICA AUTÓNOMA (DICTADO CONTINUO):
           - Si el radiólogo dicta de corrido sin emitir comandos estructurales, DEBES segmentar y REORDENAR el texto tú mismo basándote en la gramática española, la lógica académica y la legibilidad médica.
           - REGLA DEL LÍMITE: Prohibido generar bloques continuos de más de 4 líneas. Al finalizar una oración clínica larga, aplica un salto de línea físico (\\n) o un punto y aparte natural.
           - DE LO GENERAL A LO PARTICULAR: Reorganiza el discurso para que el texto fluya siempre desde las alteraciones morfológicas globales (alineación, ejes, aspectos macro) hacia los hallazgos específicos e incidentales.
           - PROTOCOLO POR NIVELES (CEFALO-CAUDAL): Si el dictado describe estructuras segmentarias (ej: niveles discales, metámeras o vértebras), agrúpalos y ordénalos obligatoriamente de forma descendente (ej: de C1 a C7, o de L1 a L5). Aunque el médico mencione un nivel superior al final del audio (orden de descubrimiento), tu salida final debe recolocarlo al inicio del bloque de niveles correspondiente. Está prohibido saltar caóticamente de niveles inferiores a superiores en la redacción final.
           - TRANSICIÓN ANATÓMICA: Inyecta un doble salto de línea (\\n\\n) cada vez que el discurso cambie de un hallazgo o zona anatómica a otra (ej: antes de 'A nivel de...', 'No se observan...', 'El parénquima...', 'Estructuras óseas...'). El texto debe ser scannable.
        
        8. PUNTUACIÓN NATIVA Y CONTROL DE SÍMBOLOS BASURA:
           - Integra de forma fluida los signos (. , : ( )). Usa el contexto probabilístico para determinar si refiere al signo o a una palabra anatómica.
           - BLOQUEO DE TOKENS RUEDO: Está terminantemente prohibido escribir símbolos literales como "n/", "n n/", "/n" o "\\n" en la salida de texto. Si detectas ruidos de respiración, carraspeos o vacilaciones ("ehmm", "y...en"), ignóralos o tradúcelos al signo de puntuación o salto de línea físico que corresponda gramaticalmente.`;

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: targetModel,
        config: {
          systemInstruction,
          safetySettings: SAFETY_SETTINGS,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: properties,
            required: requiredFields
          },
          temperature: 0.0
        },
        contents: `BORRADOR: ${text}${config.applySuggestions && recommendations ? `\nAUDITORÍA: ${recommendations}` : ''}`
      });

      logUsage(res, targetModel);
      await logLatency(LatencyModule.REPORT_GEN, startTime, 'SUCCESS', null, payloadSize, { model: targetModel });
      
      const data = robustJSONParse(res.text || "{}");
      
      return {
        technique: (config && config.technique) ? (data.technique ? normalizeMedicalText(cleanAIResponse(data.technique)) : "") : "",
        findings: (config && config.findings) ? normalizeMedicalText(cleanAIResponse(data.findings || text)) : "",
        conclusion: (config && config.conclusion) ? (data.conclusion ? normalizeMedicalText(cleanAIResponse(data.conclusion)) : "") : "",
        differential: ""
      };
    }, "Redacción de Informe"));
  } catch (e) { 
    await logLatency(LatencyModule.REPORT_GEN, startTime, 'ERROR', e, payloadSize, { model: targetModel });
    throw new Error(formatGeminiError(e, "Redacción de Informe"));
  }
};

export const generateStructuredSections = async (transcript: string, base: string | null, lang: string, mode: 'fast' | 'normal' = 'fast', useFlexiblePrompts: boolean = false, includeTitle: boolean = false): Promise<any> => { 
    const ai = getAIClient();
    const startTime = Date.now();
    const payloadSize = transcript.length + (base?.length || 0);
    const modelId = mode === 'normal' ? 'gemini-3.8-flash' : 'gemini-3.5-flash-lite';

    try {
      return await globalQueue.add(() => callGeminiWithRetry(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); 
        
        try {
          const cleanBase = base 
            ? base
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/p>/gi, '\n\n')
                .replace(/<\/div>/gi, '\n')
                .replace(/&nbsp;/g, ' ')
                .replace(/<(?!(\/?(b|strong|table|tr|td|th|thead|tbody))\b)[^>]*>/gi, '')
                .trim()
            : null;

          const systemInstruction = useFlexiblePrompts
            ? (mode === 'fast' ? FLEXIBLE_FAST_FUSION_PhD_PROMPT : FLEXIBLE_NORMAL_FUSION_PhD_PROMPT)
            : (mode === 'fast' ? FAST_FUSION_PhD_PROMPT : NORMAL_FUSION_PhD_PROMPT);

        const userContent = mode === 'fast' 
          ? `[ENTRADA DE DATOS ACTUAL]
BASE: ${cleanBase || 'DEJAR VACÍO_SI_NO_EXISTE'}
DICTADO: ${transcript}

IDIOMA: ${lang}
TITULO_SOLICITADO: ${includeTitle ? 'SI' : 'NO'}`
          : `# ENTRADA DE DATOS
BASE: ${cleanBase || 'DEJAR VACÍO_SI_NO_EXISTE'}
DICTADO: ${transcript}

IDIOMA: ${lang}
TITULO_SOLICITADO: ${includeTitle ? 'SI' : 'NO'}`;

        const modelConfig: any = {
          systemInstruction,
          safetySettings: SAFETY_SETTINGS,
          temperature: 0.1
        };
        if (mode === 'fast' || mode === 'normal') {
          modelConfig.responseMimeType = "application/json";
          modelConfig.responseSchema = {
            type: Type.OBJECT,
            properties: {
              reasoning_space: {
                type: Type.STRING,
                description: "Espacio de razonamiento y anclaje de medidas clínicas de la hipótesis contrastada."
              },
              findings: {
                type: Type.STRING,
                description: "Hallazgos detallados. OBLIGATORIO: Segmentar el texto aplicando de forma autónoma doble salto de línea físico (\\n\\n) cada 3 o 4 líneas o al cambiar de estructura anatómica. CRITERIO CLÍNICO: La calidad radiológica prima sobre la rigidez de la plantilla base; se prohíbe terminantemente duplicar o reiterar un mismo hallazgo en múltiples párrafos para forzar el encaje. Usa exclusivamente etiquetas HTML <b> para destacar diagnósticos relevantes. Prohibidos los asteriscos (*)."
              },
              conclusion: {
                type: Type.STRING,
                description: "Conclusiones diagnósticas. Separar conclusiones estrictamente con doble salto de línea físico (\\n\\n). PROHIBIDO usar números, letras de incisos o guiones."
              },
              differential: {
                type: Type.STRING,
                description: "Diagnósticos diferenciales complementarios si aplica, o un string vacío si la clínica es inequívoca."
              }
            },
            required: ["findings", "conclusion"]
          };
        }

        const res = await ai.models.generateContent({
          model: modelId,
          config: modelConfig,
          contents: userContent
         }, { signal: controller.signal } as any);
        
        clearTimeout(timeoutId);
        logUsage(res, modelId);
        await logLatency(LatencyModule.MODULAR_1_FUSION, startTime, 'SUCCESS', null, payloadSize, { model: modelId });
        
        const rawText = res.text || "";
        
        const data = robustJSONParse(rawText);
        const findings = cleanAIResponse(data?.findings || transcript);
        const conclusion = cleanAIResponse(data?.conclusion || "");
        const differential = cleanAIResponse(data?.differential || "");

        return { 
          findings: normalizeMedicalText(findings), 
          conclusion: normalizeMedicalText(conclusion), 
          differential: normalizeMedicalText(differential) 
        };
      } catch (innerE) {
        clearTimeout(timeoutId);
        throw innerE;
      } finally {
        clearTimeout(timeoutId);
      }
    }, "Procesamiento Estructural"));
  } catch (e: any) {
    await logLatency(LatencyModule.MODULAR_1_FUSION, startTime, 'ERROR', e, payloadSize, { model: modelId });
    throw new Error(formatGeminiError(e, "Procesamiento Estructural"));
  }
};


export const processPHDAssistance = async (context: string, images: { data: string, mimeType: string }[]): Promise<any> => {
  const ai = getAIClient();
  const startTime = Date.now();
  const modelId = 'gemini-3.5-flash'; // Flash 3.5 para visión diagnóstica profunda activa y veloz
  
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: `ERES UN CATEDRÁTICO RADIÓLOGO PhD ESPECIALISTA EN RADIOLOGÍA MULTIMODAL.
Analiza las imágenes proporcionadas en el contexto clínico: ${context}.

PROTOCOLO DE DIAGNÓSTICOS DIFERENCIALES:
- Enumera los diagnósticos diferenciales ordenados estrictamente de MAYOR a MENOR probabilidad/frecuencia clínica según los hallazgos observados en la imagen.
- Para cada uno, indica un porcentaje ESTIMATIVO de probabilidad (ej. "58%") basado en la prevalencia típica y la correspondencia con los hallazgos — los porcentajes de todos los diferenciales listados deben sumar aproximadamente 100%.
- EXCLUYE cualquier diagnóstico diferencial con probabilidad estimada menor al 5%. No hay límite máximo de cantidad: incluye todos los que superen ese umbral.
- Para cada uno, añade una justificación breve (1-2 frases) explicando qué hallazgo específico de imagen apoya ese diagnóstico.
- Formato de cada entrada: "N. Diagnóstico (XX%): justificación breve." Separa cada diferencial con doble salto de línea físico (\\n\\n). Prohibido usar viñetas, asteriscos o markdown.

JSON OUTPUT:
{
  "hallazgos": "descripción detallada PhD",
  "conclusion": "conclusión definitiva",
  "diferenciales": "lista de diagnósticos diferenciales ordenada, con porcentaje y justificación, según el protocolo anterior"
}`,
        responseMimeType: "application/json",
        temperature: 0.1
      },
      contents: [
        ...images.map(img => ({ inlineData: { mimeType: img.mimeType, data: img.data } })),
        { text: `Analiza este estudio de ${context}` }
      ]
    });
    
    logUsage(res, modelId);
    return robustJSONParse(res.text || "{}");
  } catch (e) {
    throw new Error(formatGeminiError(e, "Análisis PHD"));
  }
};

export const processPHDAssistanceSilent = async (data: string, mimeType: string): Promise<any> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: `Analiza brevemente la imagen radiológica.
        JSON OUTPUT:
        {
          "estudio": "tipo de estudio detectado",
          "hallazgos_certeros": ["hallazgo 1", "hallazgo 2"],
          "hallazgos_dudosos": ["hallazgo dudoso 1"],
          "sugerencia_phd": "consejo breve"
        }`,
        responseMimeType: "application/json",
        temperature: 0.1
      },
      contents: [
        { inlineData: { mimeType, data } },
        { text: "Detección rápida" }
      ]
    });
    logUsage(res, modelId);
    return robustJSONParse(res.text || "{}");
  } catch { return {}; }
};

export const generatePHDAssistantReport = async (selectedText: string, context: string, base: string | null): Promise<any> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: `Redacta un informe médico PhD basado en hallazgos seleccionados.
        CONTEXTO: ${context}
        BASE: ${base || 'Ninguna'}`,
        temperature: 0.1
      },
      contents: `Hallazgos seleccionados:\n${selectedText}`
    });
    logUsage(res, modelId);
    return res.text;
  } catch { return null; }
};

export const refineMedicalTranscription = async (raw: string): Promise<string> => {
  if (!raw) return "";
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: `ERES UN REFINADOR RADIOLÓGICO PhD. 
          Tu tarea es tomar una transcripción bruta y elevarla a nivel de informe profesional.
          
          CORRECCIONES:
          1. ORTOGRAFÍA: Corrige términos médicos y anatómicos.
          2. PUNTUACIÓN: Inserta puntos y comas para dar sentido clínico a las frases.
          3. FORMATO: Usa HTML <b> para términos clave. NO Markdown.
          4. PROHIBICIÓN: No resumas ni elimines información. Mantén la integridad del dictado original.`,
        safetySettings: SAFETY_SETTINGS,
        temperature: 0.0
      },
      contents: `REFINA:\n${raw}`
    });
    logUsage(res, modelId);
    return res.text || raw;
  } catch { return raw; }
};

export const reconcileMedicalTranscription = async (raw: string, voiceProfile?: string): Promise<string> => {
  if (!raw) return "";
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';
  
  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction: `ERES UN RECONCILIADOR RADIOLÓGICO PhD. 
        Tu misión es unificar múltiples fuentes de dictado o textos médicos en una síntesis única, coherente y de alta calidad académica.
        
        REGLAS:
        1. PRECISIÓN: No pierdas ningún dato clínico, medida o lateralidad.
        2. ESTILO: Redacción humana PhD, profesional y directa.
        3. FORMATO: Solo HTML <b>. PROHIBIDO Markdown.
        4. ESTRUCTURA: Párrafos separados por doble salto de línea (\n\n). SIN LISTAS.`,
        safetySettings: SAFETY_SETTINGS,
        temperature: 0.0
      },
      contents: `RECONCILIA:\n${raw}`
    });
    logUsage(res, modelId);
    return cleanAIResponse(res.text || raw);
  } catch (e) {
    throw new Error(formatGeminiError(e, "Consolidación de Texto"));
  }
};

export const generateSessionSummary = async (history: GeminiContent[]): Promise<string> => {
  const ai = getAIClient();
  const model = 'gemini-3.1-flash-lite';
  try {
    const res = await ai.models.generateContent({
      model,
      contents: [
        ...history.map(h => ({ 
          role: h.role, 
          parts: h.parts.map(p => ({ text: p.text || "" })) 
        })), 
        { role: 'user', parts: [{ text: "Genera la síntesis académica estructurada." }] }
      ],
      config: { 
        systemInstruction: `ERES UN CATEDRÁTICO PhD. Solo usa HTML básico (<b>).`,
        safetySettings: SAFETY_SETTINGS,
        temperature: 0.2
      }
    });
    logUsage(res, model);
    return cleanAIResponse(res.text || "");
  } catch { return ""; }
};

// Moved outside to avoid re-declaration errors and defined type
const REPORT_EXTENSION_MAP: Record<string, string> = {
  'Breve': "Extremadamente conciso, solo lo patológico o relevante.",
  'Medio': "Longitud estándar académica equilibrada.",
  'Extenso': "Muy detallado, descriptivo y exhaustivo."
};

export const processAgentCommand = async (transcript: string, contextReports: ReportTemplate[]): Promise<any> => {
  const ai = getAIClient();
  const startTime = Date.now();
  const modelId = 'gemini-3.1-flash-lite';
  
  const systemInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD DE ÉLITE ESPECIALISTA EN REDACCIÓN DE INFORMES DE ALTA COMPLEJIDAD.
  Tu tarea es transformar un dictado radiológico bruto en un informe estructurado de excelencia médica.
  
  INSTRUCCIONES:
  1. ANALIZA: El dictado puede ser fragmentario. Realíza una síntesis profesional.
  2. ESTRUCTURA: Separa en Técnica, Hallazgos, Conclusión y Diagnóstico Diferencial.
  3. FORMATO: Usa HTML <b> para términos clave. PROHIBIDO Markdown.
  4. IDIOMA: Mantén el idioma del dictado (predominantemente Español).
  
  CONEXIÓN CON PLANTILLAS (Prioriza si hay coincidencia):
  ${contextReports?.slice(0, 5).map(r => `- ${r.title}`).join('\n') || 'Ninguna proporcionada'}
  
  JSON OUTPUT SCHEMA:
  {
    "technique": "descripción de la técnica usada",
    "findings": "hallazgos detallados en prosa PhD",
    "conclusion": "conclusión diagnóstica definitiva",
    "differential": "diagnósticos diferenciales o cadena vacía"
  }`;

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: modelId,
        config: {
          systemInstruction,
          responseMimeType: "application/json",
          temperature: 0.1
        },
        contents: `DICTADO DEL RADIÓLOGO:\n${transcript}`
      });
      
      logUsage(res, modelId);
      const data = robustJSONParse(res.text || "{}");
      
      const result = {
        technique: cleanAIResponse(data.technique || ""),
        findings: cleanAIResponse(data.findings || transcript),
        conclusion: cleanAIResponse(data.conclusion || ""),
        differential: cleanAIResponse(data.differential || "")
      };

      await logLatency(LatencyModule.REPORT_GEN, startTime, 'SUCCESS', null, transcript.length, { model: modelId, mode: 'agent_command' });
      return result;
    }, "Comando de Agente Radiológico"));
  } catch (e) {
    await logLatency(LatencyModule.REPORT_GEN, startTime, 'ERROR', e, transcript.length, { model: modelId });
    throw new Error(formatGeminiError(e, "Comando de Agente"));
  }
};

export const selectAgentTemplateAndMode = async (
  transcript: string,
  reportTitles: { id: string; title: string }[]
): Promise<{ selectedReportId: string | null; fusionMode: 'fast' | 'normal'; flexibleMode: boolean }> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.1-flash-lite';

  const systemInstruction = `Eres un asistente que decide cómo procesar un dictado radiológico dentro de la aplicación.

Tu tarea tiene dos partes:
1. Elegir, de la siguiente lista de plantillas de informes base disponibles, cuál coincide mejor con el tipo de estudio que se está dictando (por título y contenido). Si NINGUNA coincide razonablemente bien, responde null en selectedReportId — nunca fuerces una coincidencia mala.
2. Decidir el modo de procesamiento más adecuado:
   - "fast": dictados breves, sencillos, de pocos hallazgos.
   - "normal": dictados extensos, con varios hallazgos o mayor complejidad clínica.
   - flexibleMode (true/false): actívalo (true) por DEFECTO en la gran mayoría de los casos. Actívalo SIEMPRE que el dictado mencione pocos hallazgos (uno o dos), ya que con flexibleMode desactivado la fusión apenas modifica la plantilla predefinida y el resultado queda pobre/poco útil. Actívalo también si el dictado usa lenguaje coloquial o terminología no estandarizada. Solo desactívalo (false) cuando el dictado sea extenso y detallado, con varios hallazgos, y ya use terminología médica precisa y estructurada que deba preservarse literal sin normalización.

PLANTILLAS DISPONIBLES:
${reportTitles.map(r => `- ID: ${r.id} | Título: ${r.title}`).join('\n')}

Responde ÚNICAMENTE en este formato JSON, sin texto adicional:
{ "selectedReportId": "<id o null>", "fusionMode": "fast" | "normal", "flexibleMode": true | false }`;

  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        temperature: 0.1
      },
      contents: `DICTADO DEL RADIÓLOGO:\n${transcript}`
    });
    logUsage(res, modelId);
    const data = robustJSONParse(res.text || "{}");
    return {
      selectedReportId: (data.selectedReportId && data.selectedReportId !== 'null') ? data.selectedReportId : null,
      fusionMode: data.fusionMode === 'normal' ? 'normal' : 'fast',
      flexibleMode: !!data.flexibleMode
    };
  } catch (e) {
    console.error("Error seleccionando plantilla/modo del agente:", e);
    return { selectedReportId: null, fusionMode: 'fast', flexibleMode: true };
  }
};

export const generateReportContent = async (options: any): Promise<string> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.8-flash';
  
  const { 
    title, 
    clinicalData, 
    styleMode, 
    extension, 
    anatomicalMode, 
    separationType, 
    includeTechnique, 
    includeConclusion, 
    includeDifferential 
  } = options;

  const isStructured = styleMode === 'structured';
  const extensionText = REPORT_EXTENSION_MAP[extension] || REPORT_EXTENSION_MAP['Medio'];
  const separationText = separationType === 'Salto de Línea' ? "Usa saltos de línea simples." : "Usa párrafos bien definidos con doble salto de línea.";

  const anatomicalRule = (isStructured && anatomicalMode)
    ? "REGLA DE ORGANIZACIÓN: Estructura los hallazgos por sistemas/órganos. Cada hallazgo debe empezar por el órgano o sistema en mayúsculas seguido de dos puntos. Ejemplo: <b>PULMONES:</b> normal."
    : isStructured 
      ? "REGLA DE ORGANIZACIÓN: Estructura el informe mediante secciones claras." 
      : "REGLA DE ORGANIZACIÓN: Redacta el informe en estilo narrativo fluido.";

  const sectionRules = [
    includeTechnique ? "INCLUYE una sección de TÉCNICA detallando el procedimiento." : "NO INCLUYAS sección de Técnica.",
    "INCLUYE siempre una sección de HALLAZGOS.",
    includeConclusion ? "INCLUYE una sección de CONCLUSIÓN sintética y precisa." : "NO INCLUYAS sección de Conclusión.",
    includeDifferential ? "INCLUYE una sección de DIAGNÓSTICO DIFERENCIAL si procede." : "NO INCLUYAS sección de Diagnóstico Diferencial."
  ].join("\n    ");

  const systemInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD DE ÉLITE.
    Tu misión es generar un informe radiológico profesional basado en un título y datos clínicos básicos.

    REGLAS CRÍTICAS:
    1. TÍTULO: El informe DEBE corresponder estrictamente al tipo de estudio indicado en el título: "${title}".
    2. FORMATO: Solo HTML básico (<b>). PROHIBIDO Markdown (**), asteriscos, guiones al inicio de línea o listas.
    3. EXTENSIÓN: ${extensionText}
    4. SEPARACIÓN: ${separationText}
    5. ${anatomicalRule}
    6. SECCIONES:
    ${sectionRules}
    7. AUTO-COMPLETADO: Si los datos clínicos son escasos, completa el informe asumiendo una exploración normal para el estudio indicado en el título, manteniendo el rigor científico.
    8. RESPUESTA: Devuelve EXCLUSIVAMENTE el cuerpo del informe. No añadas preámbulos, despedidas ni el título de nuevo.`;

  try {
    const res = await ai.models.generateContent({
      model: modelId,
      config: {
        systemInstruction,
        safetySettings: SAFETY_SETTINGS,
        temperature: 0.2
      },
      contents: `ESTUDIO: ${title}\nHALLAZGOS CLÍNICOS / DICTADO: ${clinicalData}`
    });

    logUsage(res, modelId);
    const draft = cleanAIResponse(res.text || "");

    try {
      const auditSystemInstruction = `ERES EL COMITÉ DE CALIDAD DE UN SERVICIO DE RADIOLOGÍA UNIVERSITARIO DE ÉLITE.
Tu tarea es auditar rigurosamente el siguiente borrador de informe radiológico (destinado a quedar como PLANTILLA DE REFERENCIA en una biblioteca médica, para el estudio "${title}") y devolver la VERSIÓN FINAL, CORREGIDA Y OPTIMIZADA. NO devuelvas un informe de auditoría ni comentarios — devuelve directamente el texto final del informe, listo para usarse como plantilla.

CONFIGURACIÓN OBLIGATORIA QUE EL BORRADOR DEBE CUMPLIR (verifica y CORRIGE cualquier desviación):
1. EXTENSIÓN REQUERIDA: ${extensionText}
2. SEPARACIÓN REQUERIDA: ${separationText}
3. ORGANIZACIÓN REQUERIDA: ${anatomicalRule}
4. SECCIONES REQUERIDAS:
   ${sectionRules}
   (Si al borrador le falta alguna sección requerida, AÑÁDELA. Si incluye alguna sección que fue marcada como "NO INCLUYAS", ELIMÍNALA por completo.)

CRITERIOS ADICIONALES DE AUDITORÍA:
5. Terminología médica correcta, precisa y estandarizada para el tipo de estudio indicado.
6. Coherencia anatómica total: sin contradicciones entre hallazgos, sin lateralidades erróneas.
7. Verifica que el informe efectivamente describa un estudio NORMAL para "${title}" — elimina cualquier hallazgo patológico, alarma o mención ambigua que se haya colado por error, salvo que los datos clínicos proporcionados indicaran explícitamente lo contrario.
8. Elimina redundancias, frases de relleno o descripciones genéricas de baja calidad; mejora la fluidez y precisión de la redacción.
9. FORMATO: Solo HTML básico (<b>). PROHIBIDO Markdown (**), asteriscos, guiones al inicio de línea o listas.
10. Devuelve EXCLUSIVAMENTE el cuerpo final del informe corregido. Sin preámbulos, sin explicaciones, sin encabezados de "versión final".`;

      const auditRes = await ai.models.generateContent({
        model: modelId,
        config: {
          systemInstruction: auditSystemInstruction,
          safetySettings: SAFETY_SETTINGS,
          temperature: 0.15
        },
        contents: `BORRADOR A AUDITAR Y CORREGIR:\n\n${draft}`
      });

      logUsage(auditRes, modelId);
      const finalReport = cleanAIResponse(auditRes.text || draft);
      return finalReport;
    } catch (auditError) {
      console.warn("Audit step failed in generateReportContent, returning draft:", auditError);
      return draft;
    }
  } catch (error) { 
    console.error("Gemini Service Error:", error);
    throw error; 
  }
};

/**
 * Performs post-processing on a medical report based on a specific action.
 */
export const postProcessReport = async (text: string, action: 'optimize' | 'pro' | 'translate' | 'structure' | 'narrative', targetLang?: string): Promise<string> => {
  const ai = getAIClient();
  const modelId = 'gemini-3.5-flash-lite';
  
  let strategyInstruction = "";

  switch (action) {
    case 'optimize':
      strategyInstruction = `Tu misión es RESUMIR el informe de forma real, no reformular con las mismas palabras.
TÉCNICAS DE COMPRESIÓN OBLIGATORIAS: fusiona frases que describan el mismo hallazgo o región en una sola oración; elimina reiteraciones, matizaciones redundantes y descripciones de normalidad ya implícitas en el contexto; evita frases de relleno o transición innecesarias.
OBJETIVO: reducir el texto entre un 30% y 40% de longitud real, comprimiendo información, no solo reescribiendo.
CONSERVACIÓN ABSOLUTA: nunca elimines hallazgos patológicos, medidas, lateralidades ni negaciones clínicas relevantes.
FORMATO: prosa fluida, sin listas, viñetas ni títulos.`;
      break;
    case 'structure':
      strategyInstruction = `Tu misión es ESTRUCTURAR el informe siguiendo el orden que usa un radiólogo profesional al dictar: organización sistemática y anatómica, de lo general (aspecto global, alineación, morfología) a lo particular (hallazgos específicos por estructura o nivel), en el mismo orden lógico en que se explorarían las regiones anatómicas. NO reordenes por "importancia clínica" ni pongas los hallazgos más relevantes al principio como si fuera un resumen — eso rompe el orden sistemático que espera un radiólogo.
TÍTULOS: TOTALMENTE PROHIBIDO agregar títulos innecesarios o secciones artificiales.`;
      break;
    case 'narrative':
      strategyInstruction = `Tu misión es convertir el informe en PROSA NARRATIVA fluida y natural, como la dictaría un radiólogo experto humano en su práctica diaria — NO como un ensayo académico.
PROHIBIDO: vocabulario rimbombante, grandilocuente o innecesariamente sofisticado. Usa terminología médica estándar y precisa, sin adornos retóricos.
ESTRUCTURA: de lo general a lo particular, en orden sistemático anatómico.
REDACCIÓN: prosa médica clara y directa, evitando fragmentaciones o listas.`;
      break;
    case 'pro':
      strategyInstruction = ELITE_PHD_PROMPT;
      break;
    case 'translate':
      strategyInstruction = `Traduce fielmente el informe al idioma: ${targetLang || 'Español'}. 
        NIVEL: Mantén el tono PhD y la precisión técnica del original.`;
      break;
    default:
      strategyInstruction = `Refina el informe médico con precisión académica.`;
  }

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: modelId,
        config: {
          systemInstruction: `ERES UN CATEDRÁTICO RADIÓLOGO PhD ESPECIALISTA EN REFINAMIENTO MÉDICO DE ALTA PRECISIÓN.
            
            PROTOCOLOS FUNDAMENTALES:
            1. IDENTIDAD: El resultado debe reflejar un nivel de doctorado, con terminología médica precisa y directa — sin vocabulario rimbombante ni adornos innecesarios.
            2. ESTRUCTURA: Usa OBLIGATORIAMENTE DOBLE SALTO DE LÍNEA (\\n\\n) entre párrafos para mantener la independencia visual.
            3. FORMATO: Solo HTML <b> para términos clave. ESTÁ TERMINANTEMENTE PROHIBIDO el uso de Markdown (**), asteriscos, guiones al inicio de línea, viñetas o listas numeradas.
            4. MISIÓN ESPECÍFICA: ${strategyInstruction}
            5. CONCISIÓN: Elimina cualquier muletilla, preámbulo o redundancia no clínica.`,
          safetySettings: SAFETY_SETTINGS,
          temperature: 0.1
        },
        contents: `TEXTO A PROCESAR:\n${text}`
      });

      logUsage(res, modelId);
      return cleanAIResponse(res.text || text);
    }, `Post-procesado (${action})`));
  } catch (e) {
    throw new Error(formatGeminiError(e, `Post-procesado (${action})`));
  }
};

export const transformMedicalText = async (text: string, mode: string, extension: string, styleContext: string): Promise<string> => {
    const ai = getAIClient();
    const modelId = 'gemini-3.1-flash-lite';
    try {
      return await globalQueue.add(() => callGeminiWithRetry(async () => {
        const res = await ai.models.generateContent({ 
          model: modelId, 
          config: { temperature: 0.1 },
          contents: `TRANSFORMAR: ${text}\nMODO: ${mode}`
        });
        logUsage(res, modelId);
        return cleanAIResponse(res.text || text);
      }, `Transfromación Médica (${mode})`));
    } catch (e) {
      throw new Error(formatGeminiError(e, `Transfromación Médica (${mode})`));
    }
};

export const integrateAuditSuggestions = async (findings: string, conclusion: string, recommendations: string): Promise<{ findings: string; conclusion: string }> => {
  const ai = getAIClient();
  const targetModel = 'gemini-3.5-flash';
  const startTime = Date.now();
  const payloadSize = findings.length + conclusion.length + recommendations.length;
  
  const systemInstruction = `ERES UN CATEDRÁTICO RADIÓLOGO PhD ESPECIALISTA EN REDACCIÓN Y AUDITORÍA MÉDICA.
  Tu tarea es integrar las sugerencias de la auditoría en los Hallazgos (Findings) y Conclusión (Conclusion) de un informe radiológico.
  
  REGLAS DE ORO:
  1. NO elimines información clínica relevante, medidas, lateralidades o datos anatómicos del informe original.
  2. Sigue rigurosamente las recomendaciones proporcionadas en la AUDITORÍA para corregir, pulir o reestructurar los Hallazgos y la Conclusión.
  3. FORMATO: Solo usa etiquetas HTML <b> para destacar diagnósticos relevantes o términos clave. PROHIBIDO usar Markdown (** o asteriscos).
  4. ESTRUCTURA: Separa párrafos en Hallazgos (findings) usando DOBLE SALTO DE LÍNEA REAL (\\n\\n). Redacta en prosa fluida sin listas, viñetas ni guiones.
  5. JSON OUTPUT SCHEMA: Debes responder con un objeto JSON válido con los campos "findings" y "conclusion".`;

  try {
    return await globalQueue.add(() => callGeminiWithRetry(async () => {
      const res = await ai.models.generateContent({
        model: targetModel,
        config: {
          systemInstruction,
          safetySettings: SAFETY_SETTINGS,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              findings: {
                type: Type.STRING,
                description: "Hallazgos actualizados e integrando las recomendaciones de la auditoría. Sin markdown, prosa fluida con doble salto de línea."
              },
              conclusion: {
                type: Type.STRING,
                description: "Conclusión actualizada e integrando las recomendaciones de la auditoría. En prosa limpia y fluida."
              }
            },
            required: ["findings", "conclusion"]
          },
          temperature: 0.1
        },
        contents: `HALLAZGOS ORIGINALES: ${findings}\nCONCLUSIÓN ORIGINAL: ${conclusion}\nAUDITORÍA / RECOMENDACIONES: ${recommendations}`
      });

      logUsage(res, targetModel);
      const data = robustJSONParse(res.text || "{}");
      return {
        findings: data.findings ? normalizeMedicalText(cleanAIResponse(data.findings)) : findings,
        conclusion: data.conclusion ? normalizeMedicalText(cleanAIResponse(data.conclusion)) : conclusion
      };
    }, "Integrar Auditoría"));
  } catch (e) {
    throw new Error(formatGeminiError(e, "Integrar Auditoría"));
  }
};

